/**
 * Área principal do chat com mensagens e input
 * 
 * Inclui: header com info do contato, lista de mensagens agrupadas por data,
 * reprodução de áudios/imagens, e área de input com gravação de áudio e anexos
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { AvatarWithLightbox } from '@/components/ui/avatar-with-lightbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { Label } from '@/components/ui/label';

// ===== WEBRTC SCREEN SHARE =====
import { useSupportWidgetConfig } from '@/hooks/useSupportWidgetConfig';
import { useAgentScreenShare } from '@/hooks/webrtc';
import { ScreenShareButton } from './ScreenShareButton';
import { ScreenShareViewer } from './ScreenShareViewer';
import { ScreenShareModal } from './ScreenShareModal';
import { ScreenShareWaitingDialog } from './ScreenShareWaitingDialog';
import { ContactNotesAlertBanner } from './notes/ContactNotesAlertBanner';
import { ScheduledMessagesAlertBanner } from './scheduled/ScheduledMessagesAlertBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Send,
  MoreVertical,
  Phone,
  Smartphone,
  PanelRightClose,
  PanelRightOpen,
  Loader2,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  UserPlus,
  UserCheck,
  Building,
  Users,
  ShieldAlert,
  ChevronUp,
  ChevronDown,
  Bot,
  BotOff,
  Megaphone,
  Settings,
  Zap,
  Eye,
  EyeOff,
  Tag,
  Tags,
  ArrowRightLeft,
  LayoutGrid,
  Pin,
  Search,
  PlayCircle,
  ArrowLeft,
  Paperclip,
  FileIcon,
  ExternalLink,
  AlarmClock,
  Forward,
  Lock,
} from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AssignTagsDialog } from '@/components/tenant/contacts/AssignTagsDialog';
import { InlineCategoryPicker, InlinePhasePicker } from './InlineKanbanPickers';
import { useContactSidebarDetails } from '@/hooks/useContactSidebarDetails';
import { useContactTags } from '@/hooks/useContactTags';

import { useGroupUtils } from '@/hooks/useGroupUtils';
import { useProviderUtils } from '@/hooks/useProviderUtils';
import { Conversation, Message } from '@/pages/tenant/Chat';
import { ConversationSearchSheet } from './ConversationSearchSheet';

import { Database } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
// Rascunho de mensagem deixado por outras telas (ex.: compartilhamento de proposta)
import { consumeChatDraft } from '@/lib/chatDraft';
import { useConversationComments, isInstagramComment } from '@/hooks/useConversationComments';
import { CommentPrivateReplyWarning } from './CommentPostPreview';
import { PostCardBubble } from './PostCardBubble';
import { AdSourceCardBubble } from './AdSourceCardBubble';
import { CommentQuoteBubble } from './CommentQuoteBubble';
import { StoryQuoteBubble } from './StoryQuoteBubble';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { useLabels } from '@/hooks/useLabels';
import { AssignLabelsDialog, LabelBadgeList } from '@/components/tenant/labels';
import { isLabelsSupported } from '@/lib/labelsConfig';
import { isPresenceSupported } from '@/lib/presenceConfig';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageStatusIndicator } from './MessageStatusIndicator';
import { ErrorDetailsDialog } from './ErrorDetailsDialog';
import { AudioMessageBubble } from './AudioMessageBubble';
import { AudioRecorderButton } from './AudioRecorderButton';
import { AttachmentButton } from './AttachmentButton';
import { ImageMessageBubble } from './ImageMessageBubble';
import { ImageGridBubble } from './ImageGridBubble';
import { ImageLightbox } from './ImageLightbox';
import { SendMultipleImagesDialog } from './SendMultipleImagesDialog';
import { DocumentMessageBubble } from './DocumentMessageBubble';
import { SendDocumentDialog } from './SendDocumentDialog';
import { SendVideoDialog } from './SendVideoDialog';
import { VideoMessageBubble } from './VideoMessageBubble';
import { LocationMessageBubble } from './LocationMessageBubble';
import { SendLocationDialog } from './SendLocationDialog';
import { LinkMessageBubble } from './LinkMessageBubble';
import { ContactMessageBubble } from './ContactMessageBubble';
import { SendContactDialog } from './SendContactDialog';
import { SendPixDialog } from './SendPixDialog';
import { ReplyPreview } from './ReplyPreview';
import { QuotedMessageBubble } from './QuotedMessageBubble';
import { MessageContextMenu } from './MessageContextMenu';
import { ForwardMessageSheet } from './ForwardMessageSheet';
import { Checkbox } from '@/components/ui/checkbox';
import { isMessageForwardable } from '@/lib/forwardable';
import { FORWARD_MAX_MESSAGES } from '@/hooks/useForwardMessage';
import { MessageReactions } from './MessageReactions';
import { TransferConversationDialog } from './TransferConversationDialog';
import { TransferToDepartmentDialog } from './TransferToDepartmentDialog';
import { DeletedMessageBubble } from './DeletedMessageBubble';
// Mensagem privada (nota interna) — reaproveita mensagens de sistema
import { InternalNoteBubble } from './InternalNoteBubble';
import { UserMentionPopover } from '@/components/crm/activities/UserMentionPopover';
import { useTenantMembers } from '@/hooks/useTenantMembers';

import { ButtonListMessageBubble } from './ButtonListMessageBubble';
import { OptionListMessageBubble } from './OptionListMessageBubble';
import { ImageButtonsMessageBubble } from './ImageButtonsMessageBubble';
import { SessionWindowAlert } from './SessionWindowAlert';
import { InstagramSessionExpiredAlert } from './InstagramSessionExpiredAlert';
import { TemplateMessageBubble } from './TemplateMessageBubble';
import { MissedCallMessageBubble } from './MissedCallMessageBubble';
import { CarouselMessageBubble } from './CarouselMessageBubble';
import { PixButtonMessageBubble } from './PixButtonMessageBubble';
import { QuickActionsPopover } from './QuickActionsPopover';
import { QuickActionsSheet } from './QuickActionsSheet';
import { MentionsPopover } from './MentionsPopover';
import { EmojiPicker } from './EmojiPicker';
import { ScheduleMessageButton } from './ScheduleMessageButton';
import { ChatQuickActionsFab } from './ChatQuickActionsFab';
import { InstagramSourceBadge, isInstagramSpecialType } from './InstagramSourceBadge';
import { TypingBubble } from './TypingBubble';
import { getStorageUrl } from '@/lib/storage';
import { toast } from 'sonner';
// Detecta viewport mobile para adaptar o compositor (input) e evitar quebras de layout
import { useIsMobile } from '@/hooks/use-mobile';
// Viewport "compacto" (< 380px): celulares pequenos onde o layout mobile ainda sobrepõe elementos
import { useIsCompactViewport } from '@/hooks/use-compact-viewport';

type ConversationStatus = Database['public']['Enums']['conversation_status'];

interface ChatAreaProps {
  conversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  /** Callback para enviar mensagem de texto, opcionalmente com reply, opções de comentário e @menções */
  onSendMessage: (
    content: string, 
    replyToExternalId?: string, 
    replyToProviderMessageId?: string,
    commentOptions?: {
      reply_mode?: 'direct' | 'comment_public' | 'comment_private';
      comment_id?: string;
    },
    mentions?: Array<{ phone: string; name: string }>
  ) => void;
  onSendAudio: (audioBlob: Blob, duration: number) => Promise<void>;
  /** Callback para enviar múltiplas imagens */
  onSendImages: (files: File[], caption?: string) => Promise<void>;
  /** Callback para enviar vídeo */
  onSendVideo: (file: File, caption?: string) => Promise<void>;
  onSendDocument: (file: File, customFileName?: string) => Promise<void>;
  onSendLocation: (location: {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
  }) => Promise<void>;
  onSendContact: (contact: {
    name: string;
    phone: string;
    businessDescription?: string;
  }) => Promise<void>;
  /** Callback para enviar botão PIX (chave PIX copiável) */
  onSendPix?: (pix: {
    name: string;
    key: string;
    keyType: 'CPF' | 'CNPJ' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
    message?: string;
  }) => Promise<void>;
  /** Callback para enviar reação a uma mensagem (inclui message completa com metadata.z_api_phone) */
  onSendReaction?: (message: Message, emoji: string) => Promise<void>;
  /** Callback para deletar mensagem (deleteForEveryone: true = WhatsApp + local, false = só local) */
  onDeleteMessage?: (message: Message, deleteForEveryone: boolean) => Promise<void>;
  /** Callback para reenviar mensagem que falhou */
  onResendMessage?: (message: Message) => Promise<void>;
  onUpdateStatus: (status: ConversationStatus) => void;
  onToggleSidebar: () => void;
  showSidebar: boolean;
  onTyping?: () => void;
  contactPresence?: {
    isTyping: boolean;
    isRecording: boolean;
  };
  /** ID do tenant para buscar contatos existentes */
  tenantId?: string;
  /** Callback para iniciar conversa com um contato (de mensagem compartilhada) */
  onStartConversationWithContact?: (phone: string, displayName: string) => Promise<void>;
  /** Callback para agente assumir conversa da fila (atribui a si mesmo) */
  onClaimConversation?: () => void;
  /** ID do usuário logado atual (para verificar se é o responsável) */
  currentUserId?: string;
  /** Indica se usuário é admin/supervisor (true) ou agente simples (false) */
  isAdminOrSupervisor?: boolean;
  /** Indica se usuário é tenant admin (pode deletar qualquer mensagem até 1h) */
  isTenantAdmin?: boolean;
  /** Indica se a janela de 24h do WhatsApp Oficial está expirada */
  isSessionWindowExpired?: boolean;
  /** Callback para abrir modal de seleção de template */
  onOpenTemplateDialog?: () => void;
  /** Indica se há mais mensagens antigas para carregar (paginação) */
  hasMoreMessages?: boolean;
  /** Indica se está carregando mensagens mais antigas */
  loadingMoreMessages?: boolean;
  /** Callback para carregar mensagens mais antigas */
  onLoadMoreMessages?: () => void;
  /** Contagem total de mensagens da conversa (para exibir "X de Y") */
  totalMessagesCount?: number | null;

  // ===== MODO ANCORADO (Jump-to-message / Pesquisa contextual) =====
  /** True quando a lista representa uma janela ancorada (não as últimas N msgs). */
  isAnchoredMode?: boolean;
  /** True quando existem mensagens MAIS RECENTES que a última carregada (modo ancorado). */
  hasMoreNewer?: boolean;
  /** True enquanto carrega a próxima página de mensagens MAIS RECENTES. */
  loadingMoreNewer?: boolean;
  /** Quantidade de mensagens novas (realtime) recebidas enquanto o modo ancorado está ativo. */
  newMessagesWhileAnchored?: number;
  /** ID da mensagem-alvo de um jump em andamento (para spinner inline na busca). */
  jumpingToMessageId?: string | null;
  /** Carrega a próxima página de mensagens mais recentes (modo ancorado). */
  onLoadMoreNewer?: () => void;
  /** Sai do modo ancorado e recarrega as últimas N mensagens (volta ao fim). */
  onExitAnchorMode?: () => void;
  /** Pula até uma mensagem específica carregando uma janela ancorada (~51 msgs). */
  onJumpToMessage?: (messageId: string) => Promise<boolean>;
  /** Callback para forçar refresh da conversa após ações como transferência */
  onRefreshConversation?: () => void;
  /** 
   * IDs das inboxes restritas que o agente atual pode acessar (dono da inbox)
   * Agentes vinculados a inboxes restritas podem conversar SEM precisar assumir
   */
  restrictedInboxIds?: string[];
  
  // ===== MODO OBSERVADOR =====
  /** Se true, usuário pode usar o modo observador (configurado no tenant_members) */
  canUseObserverMode?: boolean;
  
  /** Se true, usuário só pode visualizar a conversa.
   *  Input de mensagem, botões de transferência e encerramento ficam bloqueados/ocultos.
   *  Ativado quando a inbox não está na lista de inboxes acessíveis do agente (CRM/Kanban).
   */
  isReadOnlyMode?: boolean;
  /** Se true, modo observador está ativo - conversas não serão marcadas como lidas */
  isObserverModeActive?: boolean;
  /** Callback para alternar o modo observador */
  onToggleObserverMode?: () => void;
  
  // ===== HUMAN TAKEOVER - PAUSA DE BOT =====
  /** Callback para pausar/reativar bot na conversa */
  onToggleBotPause?: () => Promise<void>;
  /** Indica se está processando toggle de pausa do bot */
  isTogglingBotPause?: boolean;
  
  // ===== CONTROLE DE TRANSFERÊNCIA PENDENTE =====
  /** Indica se existe transferência pendente para o usuário atual */
  hasPendingTransfer?: boolean;
  /** Callback para iniciar atendimento (aceitar transferência) */
  onStartConversation?: () => Promise<void>;
  /** Callback para encerrar conversa */
  onCloseConversation?: () => void;
  /** Indica se está processando início de atendimento */
  isStartingConversation?: boolean;
  
  // ===== NAVEGAÇÃO MOBILE (MASTER-DETAIL) =====
  /** Callback para voltar à lista de conversas (mobile) */
  onBackToList?: () => void;
  
  // ===== WEBRTC SCREEN SHARE =====
  /** ID do tenant atual (para verificar se é tenant de suporte) */
  currentTenantId?: string;
  /** Nome do agente atual (para enviar na solicitação) */
  currentAgentName?: string;
  /** Avatar do agente atual (opcional) */
  currentAgentAvatar?: string;
  // ===== v1.2.0: Fixar/desfixar a conversa atual =====
  /** Fixa a conversa atual para o usuário logado */
  onPinConversation?: (conversationId: string) => Promise<void>;
  /** Desfixa a conversa atual para o usuário logado */
  onUnpinConversation?: (conversationId: string) => Promise<void>;
  /** True enquanto uma mutation pin/unpin está em andamento */
  isPinning?: boolean;

  // ===== MENSAGEM PRIVADA (nota interna com @menções) =====
  /**
   * Envia uma nota interna da equipe (não vai ao provider nem ao visitante).
   * `mentions` traz os user_ids mencionados no texto (`@[Nome](user_id)`).
   */
  onSendInternalNote?: (content: string, mentions: string[]) => Promise<void>;
  /** Nome do usuário logado (exibido como autor da nota interna). */
  currentUserName?: string;
}


const statusLabels: Record<ConversationStatus, string> = {
  pending: 'Pendente',
  open: 'Aberta',
  closed: 'Encerrada',
  spam: 'Spam',
};

// Ícones de status - tamanho reduzido para layout compacto do header
const statusIcons: Record<ConversationStatus, React.ReactNode> = {
  pending: <Clock className="h-2.5 w-2.5" />,
  open: <CheckCircle2 className="h-2.5 w-2.5" />,
  closed: <XCircle className="h-2.5 w-2.5" />,
  spam: <AlertTriangle className="h-2.5 w-2.5" />,
};

/**
 * Extrai a primeira URL de um texto
 */
const extractUrlFromText = (text: string): string | null => {
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const match = text.match(urlRegex);
  return match ? match[0] : null;
};

/**
 * Extrai o ID do vídeo do YouTube de qualquer formato de URL
 * Suporta todos os formatos: watch, shorts, live, embed, etc.
 * IDs do YouTube sempre têm 11 caracteres alfanuméricos (incluindo - e _)
 */
const extractYouTubeVideoId = (url: string): string | null => {
  // Verifica se é URL do YouTube
  if (!/(?:youtube\.com|youtu\.be)/i.test(url)) return null;
  
  // Padrão 1: youtu.be/VIDEO_ID
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return shortMatch[1];
  
  // Padrão 2: youtube.com com parâmetro v=VIDEO_ID
  const paramMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (paramMatch) return paramMatch[1];
  
  // Padrão 3: youtube.com/TIPO/VIDEO_ID (shorts, live, embed, v, etc.)
  const pathMatch = url.match(/youtube\.com\/(?:shorts|live|embed|v)\/([a-zA-Z0-9_-]{11})/);
  if (pathMatch) return pathMatch[1];
  
  return null;
};

/**
 * Gera URL da thumbnail do YouTube a partir do ID do vídeo
 */
const getYouTubeThumbnail = (videoId: string): string => {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

export function ChatArea({
  conversation,
  messages,
  loading,
  onSendMessage,
  onSendAudio,
  onSendImages,
  onSendVideo,
  onSendDocument,
  onSendLocation,
  onSendContact,
  onSendPix,
  onSendReaction,
  onDeleteMessage,
  onResendMessage,
  onUpdateStatus,
  onToggleSidebar,
  showSidebar,
  onTyping,
  contactPresence,
  tenantId,
  onStartConversationWithContact,
  onClaimConversation,
  currentUserId,
  isAdminOrSupervisor,
  isTenantAdmin,
  isSessionWindowExpired,
  onOpenTemplateDialog,
  hasMoreMessages,
  loadingMoreMessages,
  onLoadMoreMessages,
  totalMessagesCount,
  // ===== MODO ANCORADO =====
  isAnchoredMode,
  hasMoreNewer,
  loadingMoreNewer,
  newMessagesWhileAnchored,
  jumpingToMessageId,
  onLoadMoreNewer,
  onExitAnchorMode,
  onJumpToMessage,
  onRefreshConversation,
  restrictedInboxIds,
  canUseObserverMode,
  isObserverModeActive,
  onToggleObserverMode,
  onToggleBotPause,
  isTogglingBotPause,
  hasPendingTransfer,
  onStartConversation,
  onCloseConversation,
  isStartingConversation,
  onBackToList,
  // [NOVO] Modo somente-leitura: bloqueia envio/ações quando inbox não tem permissão
  isReadOnlyMode,
  // ===== WEBRTC SCREEN SHARE =====
  currentTenantId,
  currentAgentName,
  currentAgentAvatar,
  // v1.2.0: ações de fixar/desfixar conversa
  onPinConversation,
  onUnpinConversation,
  isPinning,
  // Mensagem privada (nota interna)
  onSendInternalNote,
  currentUserName,
}: ChatAreaProps) {
  // Hook para detectar tema (dark/light) - usado para condicionar imagem de fundo
  const { theme } = useTheme();

  const [inputValue, setInputValue] = useState('');

  // ===========================================================================
  // MENSAGEM PRIVADA (nota interna) — v1.0.0
  // Modo do composer: 'reply' envia normalmente ao contato; 'internal' grava
  // uma nota interna (mensagem de sistema com metadata.kind='internal_note').
  // ===========================================================================
  const [composerMode, setComposerMode] = useState<'reply' | 'internal'>('reply');
  const [sendingInternalNote, setSendingInternalNote] = useState(false);
  // Estado do popover de @menções de usuários do tenant (somente no modo interno)
  const [showUserMentions, setShowUserMentions] = useState(false);
  const [userMentionQuery, setUserMentionQuery] = useState('');
  const [userMentionAnchor, setUserMentionAnchor] = useState<number | null>(null);
  // Mapa nome → user_id das menções escolhidas no popover.
  // O textarea exibe apenas "@Nome" (sem UUID); o formato oficial
  // `@[Nome](uuid)` é reconstruído somente no envio, a partir deste mapa.
  const [mentionUserMap, setMentionUserMap] = useState<Record<string, string>>({});
  // Membros do tenant (cache compartilhado — sem query adicional dedicada ao chat)
  const { data: tenantMembers = [] } = useTenantMembers({
    enabled: composerMode === 'internal',
  });
  const mentionableUsers = useMemo(
    () =>
      tenantMembers.map((m) => ({
        id: m.user_id,
        full_name: m.full_name || m.email || 'Usuário',
        avatar_url: m.avatar_url,
      })),
    [tenantMembers]
  );
  const filteredMentionUsers = useMemo(() => {
    const term = userMentionQuery.trim().toLowerCase();
    const base = term
      ? mentionableUsers.filter((u) => u.full_name.toLowerCase().includes(term))
      : mentionableUsers;
    return base.slice(0, 8);
  }, [mentionableUsers, userMentionQuery]);

  // Encaminhamento de mensagens (Fase 1 — WABA): mensagens selecionadas para encaminhar.
  // Quando o usuário clica em "Encaminhar" no menu de contexto, entramos em modo de seleção
  // múltipla (checkbox em cada bolha) até o limite de FORWARD_MAX_MESSAGES.
  const [forwardingMessages, setForwardingMessages] = useState<Message[]>([]);
  const [forwardSelectionMode, setForwardSelectionMode] = useState(false);
  const [selectedForwardIds, setSelectedForwardIds] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Ref alterado para textarea (suporte a texto multilinha estilo WhatsApp)
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // ----- Encaminhamento múltiplo: helpers e efeitos -----
  /** Sai do modo seleção e limpa marcações. */
  const exitForwardSelection = useCallback(() => {
    setForwardSelectionMode(false);
    setSelectedForwardIds(new Set());
  }, []);

  /** Alterna seleção de uma mensagem, respeitando limite máximo. */
  const toggleForwardSelection = useCallback((message: Message) => {
    setSelectedForwardIds((prev) => {
      const next = new Set(prev);
      if (next.has(message.id)) {
        next.delete(message.id);
        return next;
      }
      if (next.size >= FORWARD_MAX_MESSAGES) {
        toast.error(`Limite de ${FORWARD_MAX_MESSAGES} mensagens por encaminhamento.`);
        return prev;
      }
      next.add(message.id);
      return next;
    });
  }, []);

  /** Handler do botão "Encaminhar" do menu de contexto da mensagem. */
  const handleForwardFromMenu = useCallback((message: Message) => {
    setForwardSelectionMode(true);
    setSelectedForwardIds((prev) => {
      if (prev.has(message.id)) return prev;
      if (prev.size >= FORWARD_MAX_MESSAGES) return prev;
      const next = new Set(prev);
      next.add(message.id);
      return next;
    });
  }, []);

  /** Abre o Sheet de destinos com as mensagens marcadas. */
  const handleOpenForwardSheet = useCallback(() => {
    // Captura as mensagens marcadas na ordem em que aparecem na conversa.
    const ids = selectedForwardIds;
    const picked = messages.filter((m) => ids.has(m.id));
    if (picked.length === 0) return;
    setForwardingMessages(picked);
  }, [selectedForwardIds, messages]);

  // Reseta seleção ao trocar de conversa.
  useEffect(() => {
    exitForwardSelection();
    setForwardingMessages([]);
  }, [conversation?.id, exitForwardSelection]);

  // ESC cancela seleção (apenas quando o Sheet de destinos não está aberto).
  useEffect(() => {
    if (!forwardSelectionMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && forwardingMessages.length === 0) exitForwardSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [forwardSelectionMode, forwardingMessages.length, exitForwardSelection]);




  // ============================================================================
  // Layout definitivo (estilo WhatsApp Web): input como overlay + fundo até o final
  //
  // Por que isso existe?
  // - Se o input fica em fluxo normal, ele SEMPRE reserva altura fora do ScrollArea.
  // - Como o fundo/pattern está no ScrollArea, sobra uma área “sem fundo” no rodapé.
  // - Resultado: aparece a faixa branca/cinza que você está vendo.
  //
  // Solução:
  // 1) Um wrapper (corpo do chat) com o fundo WhatsApp cobrindo 100% da área.
  // 2) O input vira um overlay absoluto dentro desse wrapper.
  // 3) Medimos a altura real do overlay e aplicamos padding-bottom no conteúdo
  //    scrollável para a última mensagem nunca ficar escondida atrás do input.
  // ============================================================================
  // Viewport mobile: usado para simplificar a barra de input em telas estreitas
  const isMobile = useIsMobile();
  // Telas muito estreitas (< 380px): move ações do header para o menu e compacta o composer
  const isCompact = useIsCompactViewport();

  const inputOverlayRef = useRef<HTMLDivElement>(null);
  const [inputOverlayHeight, setInputOverlayHeight] = useState(0);
  // Estado do menu flutuante (FAB) lateral: quando aberto, recua mensagens para a direita
  // Estado do FAB de ações rápidas (persistido em localStorage pelo próprio FAB).
  // Inicia como `true` para evitar flash visual antes do callback onOpenChange disparar.
  const [fabOpen, setFabOpen] = useState(() => {
    try {
      const stored = localStorage.getItem('chat.quickActionsFab.open');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const INPUT_OVERLAY_EXTRA_PADDING_PX = 12;
  // Ref para rastrear se já fez o scroll inicial desta conversa
  const hasScrolledRef = useRef(false);
  // Refs para preservar posição visual ao clicar em "Carregar mensagens anteriores".
  // Capturamos scrollHeight/scrollTop ANTES do load; ao detectar o update de messages
  // com a flag ligada, ajustamos scrollTop pelo delta de altura — mantendo a
  // mensagem que estava visível no mesmo ponto da tela (padrão WhatsApp Web).
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const isLoadingOlderRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  // ===== MODO HISTÓRICO (history browsing) =====
  // Quando usuário clica "Carregar mensagens anteriores", ativamos esse modo
  // para que novas mensagens (realtime) NÃO empurrem a viewport para o fim.
  // Em vez disso, mostramos pílula "X novas mensagens — Ir para o fim"
  // (mesmo padrão UX já usado pelo modo ancorado de pesquisa).
  const [isHistoryBrowsingMode, setIsHistoryBrowsingMode] = useState(false);
  const [historyNewMessagesCount, setHistoryNewMessagesCount] = useState(0);
  // Refs espelho para uso dentro do efeito sem stale closure
  const isHistoryBrowsingModeRef = useRef(false);
  useEffect(() => { isHistoryBrowsingModeRef.current = isHistoryBrowsingMode; }, [isHistoryBrowsingMode]);
  // Último ID conhecido da lista (usado para detectar append real no fim)
  const lastKnownLastMessageIdRef = useRef<string | null>(null);
  // Estado para mensagem sendo respondida (reply)
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  // Refs para scroll até mensagens específicas
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // Alvo de scroll pendente para jump-to-message (pesquisa contextual).
  // Setado após a RPC ancorada resolver, consumido por useEffect quando o
  // React commitar a nova lista e o ref do elemento estiver disponível.
  // Evita race com setTimeout/requestAnimationFrame "no escuro".
  const pendingScrollTargetRef = useRef<string | null>(null);
  const pendingScrollAttemptsRef = useRef(0);
  
  // ===== AÇÕES RÁPIDAS VIA ATALHO "/" =====
  // Estado para controlar popover de ações rápidas
  const [showQuickActions, setShowQuickActions] = useState(false);
  // Estado do Sheet lateral de Ações Rápidas (botão de raio ao lado do áudio)
  const [showQuickActionsSheet, setShowQuickActionsSheet] = useState(false);
  // Texto de busca (parte após "/")
  const [quickActionSearch, setQuickActionSearch] = useState('');
  // Sheet de pesquisa contextual de mensagens na conversa (estilo WhatsApp Web)
  const [showSearchSheet, setShowSearchSheet] = useState(false);


  // ===== @MENÇÕES (apenas em grupos) =====
  // showMentions: visibilidade do popover; mentionQuery: texto após "@"
  // mentionAnchor: índice do "@" no inputValue (para reposicionar o substring no select)
  // selectedMentions: phone -> name dos contatos marcados (usado para gerar
  // o "wire text" com @<phone> no envio para o WhatsApp).
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState<number | null>(null);
  const [selectedMentions, setSelectedMentions] = useState<Map<string, string>>(new Map());
  
  // Hook para operações de grupos (verificar permissões)
  const { getGroupParticipants } = useGroupUtils();
  // Estado para controlar se usuário pode enviar mensagens em grupos
  const [canSendInGroup, setCanSendInGroup] = useState<boolean>(true);
  const [groupPermissionMessage, setGroupPermissionMessage] = useState<string>('');

  /**
   * Função para ajustar altura do textarea automaticamente
   * Comportamento idêntico ao WhatsApp Web:
   * - Altura mínima: 40px (1 linha)
   * - Altura máxima: 160px (~6 linhas)
   * - Scroll interno quando atinge máximo
   */
  const adjustTextareaHeight = () => {
    const textarea = inputRef.current;
    if (textarea) {
      // Campo vazio: mantém altura mínima (evita que placeholder longo,
      // ao quebrar em várias linhas no mobile, estique a caixa de digitação)
      if (!textarea.value) {
        textarea.style.height = '40px';
        return;
      }
      // Reset para medir o scrollHeight real
      textarea.style.height = 'auto';
      // Aplica nova altura respeitando min/max
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 40), 160);
      textarea.style.height = `${newHeight}px`;
    }
  };

  // Auto-resize do textarea quando valor muda
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  /**
   * Rascunho externo (ex.: compartilhamento de proposta): quando outra tela
   * deixa um texto pronto para esta conversa, ele é consumido uma única vez e
   * pré-preenche o campo de digitação. O usuário revisa e envia manualmente.
   */
  useEffect(() => {
    const convId = conversation?.id;
    if (!convId) return;
    const draft = consumeChatDraft(convId);
    if (draft) setInputValue(draft);
  }, [conversation?.id]);


  
  
  // Hook para detectar comentários na conversa
  const {
    hasComments,
    lastComment,
    canSendPrivateReply: canSendPrivate,
    postEntries,
    getPostForComment,
    isCommentMessage,
  } = useConversationComments(messages);
  
  // Refs para cards de postagem (para scroll ao clicar no quote)
  const postCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Verifica se é inbox do Instagram
  const isInstagramInbox = conversation?.inbox?.channel_type === 'instagram';

  // Estados para imagens (múltiplas)
  const [selectedImageFiles, setSelectedImageFiles] = useState<File[]>([]);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [lightboxData, setLightboxData] = useState<{ 
    images: string[]; 
    initialIndex: number; 
    caption?: string | null 
  } | null>(null);

  // Estados para documentos
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<File | null>(null);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);

  // Estados para vídeos
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);

  // Estado para localização
  const [showLocationDialog, setShowLocationDialog] = useState(false);

  // Estado para contato
  const [showContactDialog, setShowContactDialog] = useState(false);

  // Estado para PIX
  const [showPixDialog, setShowPixDialog] = useState(false);

  // Estados para transferência de conversa
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showTransferDeptDialog, setShowTransferDeptDialog] = useState(false);
  
  // Estado para drag-and-drop de arquivos
  const [isDragging, setIsDragging] = useState(false);
  
  // Estados para dialog de detalhes de erro de mensagem
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [selectedErrorDetails, setSelectedErrorDetails] = useState<{
    error: any;
    messageType: string;
    sentAt: string;
    providerSlug?: string; // Slug do provider da inbox para busca no catálogo de erros
  } | null>(null);
  
  // ===== WEBRTC SCREEN SHARE - HOOKS E ESTADO =====
  // Estado para modal fullscreen de screen share
  const [showScreenShareModal, setShowScreenShareModal] = useState(false);
  
  // Hook de configuração do widget de suporte
  const { config: supportConfig } = useSupportWidgetConfig();
  
  // Verifica se deve mostrar botão de acesso remoto:
  // 1. remote_access_enabled habilitado na config
  // 2. Widget de suporte habilitado
  // 3. Tenant atual é o tenant de suporte configurado
  // 4. Conversa individual (não grupo)
  const showRemoteAccessButton = !!(
    supportConfig?.remote_access_enabled &&
    supportConfig?.enabled &&
    supportConfig?.tenant_id === currentTenantId &&
    conversation &&
    !conversation.group_id
  );
  
  // Hook de screen share (só inicializa se permitido)
  const agentScreenShare = useAgentScreenShare({
    conversationId: showRemoteAccessButton ? conversation?.id ?? null : null,
    agentName: currentAgentName ?? 'Atendente',
    agentAvatar: currentAgentAvatar,
    enabled: showRemoteAccessButton,
  });

  /**
   * Handler para abrir dialog de detalhes do erro de uma mensagem
   * Compatível com estrutura legada (rejection_error) e nova (error)
   */
  const handleErrorClick = async (message: Message) => {
    // Tipagem para estrutura legada (rejection_error)
    interface LegacyRejectionError {
      code?: string;
      message?: string;
      rejected_at?: string;
    }
    
    // Normaliza dados de erro para compatibilidade com estrutura antiga (rejection_error)
    const rejectionError = message.metadata?.rejection_error as LegacyRejectionError | undefined;
    let errorData = message.metadata?.error 
      || (rejectionError ? {
          // Converte estrutura legada para nova estrutura esperada pelo ErrorDetailsDialog
          code: rejectionError.code,
          title: rejectionError.message || 'Erro do Provider',
          details: rejectionError.message,
          occurred_at: rejectionError.rejected_at,
        } : null);
    
    // === Encaminhamento: enriquecer com erro real do chat_forward_jobs ===
    // Mensagens encaminhadas com falha registram o erro real (ex.: Meta WABA)
    // em chat_forward_jobs.error_summary. Aqui buscamos esse erro para
    // substituir o fallback genérico "Detalhes não disponíveis".
    const forwardJobId = (message.metadata as Record<string, unknown> | undefined)?.forward_job_id as string | undefined;
    const forwardedFromId = (message.metadata as Record<string, unknown> | undefined)?.forwarded_from_message_id as string | undefined;
    if (!errorData && message.status === 'failed' && forwardJobId && forwardedFromId && message.conversation_id) {
      try {
        const { data: jobRow } = await supabase
          .from('chat_forward_jobs')
          .select('error_summary')
          .eq('id', forwardJobId)
          .maybeSingle();
        const summary = (jobRow?.error_summary as unknown as Array<{
          source_message_id: string;
          target_conversation_id: string;
          error: string;
          at: string;
        }> | null) ?? [];
        const match = summary.find(
          (e) => e?.source_message_id === forwardedFromId && e?.target_conversation_id === message.conversation_id,
        );
        if (match?.error) {
          // Tenta extrair código numérico do início "[100] ..."
          const codeMatch = /^\s*\[([^\]]+)\]\s*/.exec(match.error);
          errorData = {
            code: codeMatch?.[1] ?? 'FORWARD_ERROR',
            title: 'Falha ao encaminhar mensagem',
            details: match.error,
            occurred_at: match.at,
          };
        }
      } catch {
        // silencioso — cai no fallback genérico abaixo
      }
    }
    
    // Se status é 'failed' mas não há metadata de erro, cria mensagem genérica
    // Isso trata mensagens antigas que foram marcadas como failed sem metadata
    if (!errorData && message.status === 'failed') {
      errorData = {
        code: 'UNKNOWN',
        title: 'Erro no envio',
        details: 'Detalhes do erro não disponíveis. O erro ocorreu antes da implementação do registro detalhado.',
        occurred_at: message.sent_at,
      };
    }
    
    if (errorData) {
      setSelectedErrorDetails({
        error: errorData,
        messageType: message.message_type,
        sentAt: message.sent_at,
        providerSlug: conversation?.inbox?.provider_slug, // Provider da inbox para match no catálogo
      });
      setErrorDialogOpen(true);
    }
  };
  
  // Estado para dialog de etiquetas
  const [showLabelsDialog, setShowLabelsDialog] = useState(false);
  const queryClient = useQueryClient();

  // ===== Atalhos do header =====
  // Dialog de tags do contato (atalho no header)
  const [showContactTagsDialog, setShowContactTagsDialog] = useState(false);
  // Categoria Kanban em preview (compartilhado entre os pickers de fase/categoria)
  const [headerPendingCategoryId, setHeaderPendingCategoryId] = useState<string | null>(null);

  // Fonte oficial de kanban_phase para o popover do header: reaproveita a mesma
  // RPC consumida pelo sidebar (useContactSidebarDetails). Assim, quando o
  // InlinePhasePicker invalida a query 'contact-sidebar-details', o popover do
  // header também é atualizado automaticamente — sem precisar F5.
  // Obs.: a query é deduplicada pelo TanStack (mesma queryKey do sidebar).
  const { data: headerSidebarDetails } = useContactSidebarDetails({
    contactId: conversation?.contact?.id,
    inboxId: conversation?.inbox_id,
    conversationId: conversation?.id,
    enabled: !!conversation?.contact?.id && !!conversation?.inbox_id,
  });
  const headerKanbanPhase =
    headerSidebarDetails?.conversation?.kanban_phase ?? conversation?.kanban_phase ?? null;

  // Memoizamos o objeto contact passado ao AssignTagsDialog para evitar que sua
  // referência mude a cada render. Sem isso, o useEffect interno do dialog
  // reseta a seleção (selectedTags) sempre que o pai re-renderiza, impedindo
  // marcar/desmarcar tags pelo atalho do header.
  // FONTE ÚNICA DE VERDADE: usa headerSidebarDetails.contact_tags (RPC) ao invés
  // da prop conversation.contact.tags, que pode estar stale e causar erro 23505
  // (duplicate key) ao salvar tags já existentes no banco.
  const contactTagsDialogContact = useMemo(() => {
    if (!conversation?.contact) return null;
    const rpcTags = headerSidebarDetails?.contact_tags;
    const tags = Array.isArray(rpcTags)
      ? rpcTags.map((t: any) => ({ id: t.id, name: t.name, color: t.color || '#6366f1' }))
      : (conversation.contact.tags || []);
    return {
      id: conversation.contact.id,
      name: conversation.contact.name,
      tags,
    };
  }, [
    conversation?.contact?.id,
    conversation?.contact?.name,
    conversation?.contact?.tags,
    headerSidebarDetails?.contact_tags,
  ]);


  // Tenant efetivo do chat.
  // Correção 2026-08-29: a prop `currentTenantId` nunca é enviada pelas telas
  // (Chat.tsx e ChatConversationModal.tsx passam apenas `tenantId`), o que
  // fazia a listagem de tags nunca carregar e o dialog receber tenantId vazio
  // (erro "Erro ao carregar tags"). Usamos o fallback para `tenantId`.
  const effectiveTenantId = currentTenantId || tenantId;

  // Busca tags disponíveis do tenant para o dialog de tags do contato (atalho do header)
  // Reaproveita o hook centralizado (RPC get_tenant_contact_tags)
  const { data: availableContactTags = [] } = useContactTags(
    effectiveTenantId,
    showContactTagsDialog,
  );

  
  // Verifica se provider suporta etiquetas
  const labelsSupported = isLabelsSupported(conversation?.inbox?.provider_slug);
  
  // Hook para gerenciamento de etiquetas - só ativa se provider suporta
  const { 
    labels: availableLabels, 
    useConversationLabels, 
    assignLabels, 
    assigningLabels 
  } = useLabels({ 
    inboxId: conversation?.inbox_id || null, 
    enabled: !!conversation?.inbox_id && labelsSupported 
  });
  
  // Busca etiquetas atribuídas à conversa atual (só se provider suporta)
  const { data: conversationLabels = [] } = useConversationLabels(
    labelsSupported ? conversation?.id : null
  );

  /**
   * Helper para encontrar a reação do agente atual em uma mensagem
   * Normaliza identificadores para comparação consistente
   */
  const getMyReaction = (message: Message): string | undefined => {
    if (!message.reactions?.length) return undefined;
    const myId = conversation?.inbox?.identifier?.replace(/\D/g, '') || '';
    return message.reactions.find(r => 
      r.reacted_by.replace(/\D/g, '') === myId || r.reacted_by === 'agent'
    )?.emoji;
  };

  /**
   * Helper para obter nome do participante em grupos.
   * Ordem de prioridade:
   *   1. metadata.sender_name (gravado pelo webhook no INSERT da mensagem;
   *      mantido pela trigger update_messages_sender_name_on_contact_change
   *      quando o contato e cadastrado/editado)
   *   2. pushName do payload do provider (waha_payload.pushName / senderName)
   *   3. Telefone formatado (fallback final)
   */
  const getParticipantName = (message: Message): string => {
    const meta = (message.metadata || {}) as Record<string, any>;

    // 1. Nome do contato resolvido no servidor
    const senderName = typeof meta.sender_name === 'string' ? meta.sender_name.trim() : '';
    if (senderName) return senderName;

    // 2. Push name do provider (WAHA usa waha_payload.pushName; Z-API usa senderName)
    const wahaPush = meta.waha_payload && typeof meta.waha_payload === 'object'
      ? (meta.waha_payload as Record<string, any>).pushName
      : null;
    const pushName = (typeof wahaPush === 'string' && wahaPush.trim())
      || (typeof meta.senderName === 'string' && meta.senderName.trim())
      || '';
    if (pushName) return pushName as string;

    // 3. Fallback: telefone formatado
    const participantPhone = message.participant_phone;
    if (!participantPhone) return 'Desconhecido';
    const cleanPhone = participantPhone.replace(/\D/g, '');
    if (cleanPhone.length === 13 && cleanPhone.startsWith('55')) {
      const ddd = cleanPhone.slice(2, 4);
      const part1 = cleanPhone.slice(4, 9);
      const part2 = cleanPhone.slice(9);
      return `+55 (${ddd}) ${part1}-${part2}`;
    }
    return `+${cleanPhone}`;
  };

  /**
   * Helper para obter avatar do participante em grupos.
   * Faz lookup no mapa groupContactsByPhone (carregado de chat_group_participants -> contacts).
   * Retorna a URL do avatar (se cadastrado no CRM) e o nome resolvido para iniciais de fallback.
   */
  const getParticipantAvatar = (message: Message): { src?: string | null; name: string } => {
    const name = getParticipantName(message);
    const phone = (message.participant_phone || '').replace(/\D/g, '');
    if (!phone) return { src: null, name };
    const contact = groupContactsByPhone.get(phone);
    return { src: contact?.avatar_url || null, name };
  };

  /**
   * Renderiza um texto substituindo tokens "@<digitos>" pelo nome do contato
   * (em destaque) quando possível. Usado em mensagens de grupo para mostrar
   * "@Wenisteen Magno" ao invés de "@134622126059605".
   *
   * Resolução por prioridade:
   *   1. lidByCode (LID do WhatsApp).
   *   2. groupContactsByPhone (telefone E.164 normalizado).
   *   3. Sem match -> mantém o texto original "@<digitos>".
   *
   * Retorna ReactNode com tokens convertidos. Idempotente para textos sem "@".
   */
  const renderTextWithMentions = (text: string | null | undefined): React.ReactNode => {
    if (!text) return text;
    // Em conversas individuais (sem group_id) não há ganho em fazer o trabalho.
    if (!conversation?.group_id) return text;

    const re = /@(\d{5,})/g;
    const out: React.ReactNode[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    let key = 0;

    while ((m = re.exec(text)) !== null) {
      const code = m[1];
      const start = m.index;
      // Empurra trecho cru anterior ao token
      if (start > lastIndex) {
        out.push(text.slice(lastIndex, start));
      }

      // Tenta resolver por LID, depois por telefone
      const byLid = lidByCode.get(code);
      const byPhone = groupContactsByPhone.get(code);
      const resolvedName =
        (byLid?.name && String(byLid.name).trim()) ||
        (byPhone?.name && String(byPhone.name).trim()) ||
        null;

      if (resolvedName) {
        out.push(
          <span key={`mention-${key++}-${start}`} className="text-primary font-medium">
            @{resolvedName}
          </span>
        );
      } else {
        // Sem mapeamento: mantém o texto original
        out.push(m[0]);
      }
      lastIndex = start + m[0].length;
    }

    // Restante após o último match
    if (lastIndex < text.length) {
      out.push(text.slice(lastIndex));
    }
    return out;
  };

  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: instant ? 'instant' : 'smooth' 
    });
  };

  // Hook para utilitários de provider (inclui inscrição de presença)
  const { subscribePresence } = useProviderUtils();

  // =========================================================================
  // RELEASE: 2026-05-05 v1.1 - Avatares + @menções em grupos
  // - groupContactsByPhone: phone -> { name, avatar_url } (avatar do remetente
  //   no lado esquerdo das mensagens inbound).
  // - lidByCode: código LID (numérico cru) -> { name, phone } usado para
  //   resolver "@<lid>" presente no texto das mensagens (formato WAHA/Z-API).
  // - groupParticipants: lista completa para alimentar o seletor de @menções
  //   no input (mesmo participantes sem contato CRM aparecem com telefone).
  // =========================================================================
  const [groupContactsByPhone, setGroupContactsByPhone] = useState<
    Map<string, { name?: string | null; avatar_url?: string | null }>
  >(new Map());
  const [lidByCode, setLidByCode] = useState<
    Map<string, { name?: string | null; phone?: string | null }>
  >(new Map());
  const [groupParticipants, setGroupParticipants] = useState<
    Array<{ phone: string; name: string; avatar_url?: string | null }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    const groupId = conversation?.group_id;
    const inboxId = conversation?.inbox_id;

    // Sem grupo -> limpa estados
    if (!groupId) {
      setGroupContactsByPhone(new Map());
      setLidByCode(new Map());
      setGroupParticipants([]);
      return;
    }

    const loadGroupData = async () => {
      // 1. Participantes do grupo + contato CRM (via FK contact_id)
      const { data: parts, error } = await supabase
        .from('chat_group_participants')
        .select('participant_phone, contacts:contact_id(id, name, avatar_url)')
        .eq('group_id', groupId);

      if (cancelled) return;
      if (error) {
        logger.warn('[ChatArea] Falha ao carregar contatos do grupo:', error.message);
        return;
      }

      const phoneMap = new Map<string, { name?: string | null; avatar_url?: string | null }>();
      const participants: Array<{ phone: string; name: string; avatar_url?: string | null }> = [];
      const contactIds: string[] = [];

      (parts || []).forEach((row: any) => {
        const phone = (row.participant_phone || '').replace(/\D/g, '');
        if (!phone) return;
        const contact = row.contacts || null;
        if (contact?.name || contact?.avatar_url) {
          phoneMap.set(phone, { name: contact.name, avatar_url: contact.avatar_url });
        }
        if (contact?.id) contactIds.push(contact.id);
        // Nome para exibição: contato CRM se houver, senão telefone formatado
        const displayName = (contact?.name && String(contact.name).trim()) || `+${phone}`;
        participants.push({ phone, name: displayName, avatar_url: contact?.avatar_url || null });
      });

      // Ordena por nome (CRM primeiro, depois por nome A-Z)
      participants.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      setGroupContactsByPhone(phoneMap);
      setGroupParticipants(participants);

      // 2. Mapa LID -> contato (resolve "@<lid>" no texto das mensagens)
      // Filtra por inbox + contatos dos participantes do grupo (escopo enxuto).
      if (inboxId && contactIds.length > 0) {
        const { data: lids, error: lidErr } = await supabase
          .from('contact_lid_mapping')
          .select('lid, phone, contacts:contact_id(name)')
          .eq('inbox_id', inboxId)
          .in('contact_id', contactIds);

        if (cancelled) return;
        if (lidErr) {
          logger.warn('[ChatArea] Falha ao carregar LIDs do grupo:', lidErr.message);
        } else {
          const lidMap = new Map<string, { name?: string | null; phone?: string | null }>();
          (lids || []).forEach((row: any) => {
            const code = String(row.lid || '').replace(/\D/g, '');
            if (!code) return;
            const name = row.contacts?.name || null;
            lidMap.set(code, { name, phone: row.phone || null });
          });
          setLidByCode(lidMap);
        }
      } else {
        setLidByCode(new Map());
      }
    };

    loadGroupData();
    return () => {
      cancelled = true;
    };
  }, [conversation?.group_id, conversation?.inbox_id]);

  // Reseta flag de scroll e reply quando conversa muda
  useEffect(() => {
    hasScrolledRef.current = false;
    setReplyingTo(null);
    messageRefs.current.clear();
    // Reset do modo histórico ao trocar de conversa
    setIsHistoryBrowsingMode(false);
    setHistoryNewMessagesCount(0);
    lastKnownLastMessageIdRef.current = null;
  }, [conversation?.id]);

  // =========================================================================
  // RELEASE: 2026-02-08 - Inscrição para eventos de presença ao abrir conversa
  // Chama provider-proxy subscribe_presence para Z-API/WAHA ativarem webhooks
  // =========================================================================
  useEffect(() => {
    // Só inscreve para conversas individuais (não grupos) com contato e inbox
    if (!conversation?.contact?.phone || !conversation?.inbox_id) return;
    if (conversation.group_id) return; // Ignorar grupos
    // Presença só é suportada por z-api e waha (meta-whatsapp não suporta)
    if (!isPresenceSupported(conversation?.inbox?.provider_slug)) return;
    
    // Inscrever para eventos de presença deste contato
    subscribePresence(conversation.inbox_id, conversation.contact.phone);
    
  }, [conversation?.id, conversation?.contact?.phone, conversation?.inbox_id, conversation?.group_id, conversation?.inbox?.provider_slug, subscribePresence]);

  /**
   * Verifica permissões de envio em grupos
   * - Grupos readonly: ninguém pode enviar
   * - Grupos announcement: apenas admins podem enviar
   */
  useEffect(() => {
    const checkGroupPermissions = async () => {
      // Se não é grupo, pode enviar normalmente
      if (!conversation?.group_id || !conversation.group || !conversation.inbox_id) {
        setCanSendInGroup(true);
        setGroupPermissionMessage('');
        return;
      }

      const group = conversation.group;

      // Grupo é somente leitura (readonly) - ninguém pode enviar
      if (group.is_readonly) {
        setCanSendInGroup(false);
        setGroupPermissionMessage('Este grupo é somente leitura. Mensagens não podem ser enviadas.');
        return;
      }

      // Grupo é announcement (apenas admins podem enviar)
      if (group.is_announcement) {
        try {
          // Busca participantes do grupo para verificar se inbox é admin
          const participants = await getGroupParticipants(conversation.inbox_id, group.external_id);
          const inboxPhone = conversation.inbox.identifier?.replace(/\D/g, '');
          
          // Verifica se o número da inbox está entre os participantes e é admin
          const inboxParticipant = participants.find(
            p => p.participant_phone?.replace(/\D/g, '') === inboxPhone
          );

          if (!inboxParticipant || !inboxParticipant.is_admin) {
            setCanSendInGroup(false);
            setGroupPermissionMessage('Apenas administradores podem enviar mensagens neste grupo.');
            return;
          }
        } catch (error) {
          console.error('Erro ao verificar permissões do grupo:', error);
          // Em caso de erro, permite envio (fail-safe)
        }
      }

      // Se passou por todas as verificações, pode enviar
      setCanSendInGroup(true);
      setGroupPermissionMessage('');
    };

    checkGroupPermissions();
  }, [conversation?.id, conversation?.group_id, conversation?.group, conversation?.inbox_id, conversation?.inbox.identifier, getGroupParticipants]);

  // Scroll quando mensagens carregam/mudam
  useEffect(() => {
    if (messages.length === 0) {
      lastKnownLastMessageIdRef.current = null;
      return;
    }

    const currentLastId = messages[messages.length - 1]?.id ?? null;
    const previousLastId = lastKnownLastMessageIdRef.current;

    // Caso 1: load de mensagens anteriores (botão "Carregar anteriores").
    // NÃO rola para o final — preserva a posição visual ajustando scrollTop
    // pelo delta de altura introduzido pelo prepend das mensagens antigas.
    if (isLoadingOlderRef.current) {
      isLoadingOlderRef.current = false;
      const vp = scrollViewportRef.current;
      if (vp) {
        // requestAnimationFrame garante que o DOM já aplicou a nova altura
        requestAnimationFrame(() => {
          const delta = vp.scrollHeight - prevScrollHeightRef.current;
          vp.scrollTop = prevScrollTopRef.current + delta;
        });
      }
      lastKnownLastMessageIdRef.current = currentLastId;
      return;
    }

    // Caso 2: primeira renderização da conversa -> ancorar no final (instant)
    if (!hasScrolledRef.current) {
      setTimeout(() => scrollToBottom(true), 0);
      hasScrolledRef.current = true;
      lastKnownLastMessageIdRef.current = currentLastId;
      return;
    }

    // Detecta se houve append real (mudou o último ID)
    const didAppend = previousLastId !== null && currentLastId !== previousLastId;

    // Caso 3: modo histórico ativo — usuário está navegando em mensagens antigas.
    // Se chegou nova mensagem, classifica:
    //  - própria outbound do agente logado: rola ao fim e sai do modo (feedback)
    //  - outras: incrementa contador, NÃO rola (pílula assume controle)
    if (isHistoryBrowsingModeRef.current) {
      if (didAppend) {
        const newMsg = messages[messages.length - 1];
        const isOwnOutbound =
          newMsg?.direction === 'outbound' &&
          newMsg?.sender_type === 'agent' &&
          !!currentUserId &&
          newMsg?.sender_id === currentUserId;

        if (isOwnOutbound) {
          setIsHistoryBrowsingMode(false);
          setHistoryNewMessagesCount(0);
          scrollToBottom(false);
        } else {
          setHistoryNewMessagesCount((c) => c + 1);
        }
      }
      lastKnownLastMessageIdRef.current = currentLastId;
      return;
    }

    // Caso 4: padrão (sem modo histórico) — comportamento original.
    // Só rola se houve append real OU se ainda não temos baseline (primeiro update após initial).
    if (didAppend || previousLastId === null) {
      scrollToBottom(false);
    }
    lastKnownLastMessageIdRef.current = currentLastId;
  }, [messages, currentUserId]);

  // ===========================================================================
  // Saída automática do modo histórico ao rolar manualmente até próximo do fim.
  // Threshold: 80px da base. Reseta contador junto.
  // ===========================================================================
  useEffect(() => {
    const vp = scrollViewportRef.current;
    if (!vp) return;
    const onScroll = () => {
      if (!isHistoryBrowsingModeRef.current) return;
      const distanceFromBottom = vp.scrollHeight - vp.scrollTop - vp.clientHeight;
      if (distanceFromBottom < 80) {
        setIsHistoryBrowsingMode(false);
        setHistoryNewMessagesCount(0);
      }
    };
    vp.addEventListener('scroll', onScroll, { passive: true });
    return () => vp.removeEventListener('scroll', onScroll);
  }, [conversation?.id]);

  // Focus input when conversation changes
  useEffect(() => {
    inputRef.current?.focus();
  }, [conversation?.id]);

  /**
   * Mantém a altura do overlay do input sempre atualizada.
   * Usamos ResizeObserver para ser robusto (reply preview, alertas, modo observador, etc.).
   */
  useEffect(() => {
    const el = inputOverlayRef.current;
    if (!el) return;

    const updateHeight = () => {
      // getBoundingClientRect é o jeito mais confiável (inclui padding, etc.)
      const next = Math.ceil(el.getBoundingClientRect().height);
      setInputOverlayHeight(next);
    };

    updateHeight();

    const ro = new ResizeObserver(() => updateHeight());
    ro.observe(el);

    // ResizeObserver cobre mudanças no DOM; aqui cobrimos mudanças de viewport (ex.: zoom/orientação)
    window.addEventListener('resize', updateHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [conversation?.id]);

  // Envia mensagem de texto (opcionalmente como reply, com suporte a comentários Instagram)
  // LÓGICA SIMPLIFICADA: Tipo de resposta determinado automaticamente pelo "replyingTo"
  // - Respondendo a comentário → resposta pública (comentário)
  // - Respondendo a Direct ou digitando direto → Direct Message
  /**
   * Envia a nota interna (mensagem privada) — não vai ao provider.
   * O textarea contém apenas "@Nome"; aqui o texto é reconstruído para o
   * formato oficial `@[Nome](uuid)` usando o mapa das menções escolhidas.
   * Nomes que não estejam no mapa permanecem como texto puro (não viram menção).
   */
  const handleSendInternalNote = async () => {
    const raw = inputValue.trim();
    if (!raw || !onSendInternalNote || sendingInternalNote) return;

    // Reconstrói as menções: percorre os nomes conhecidos (maiores primeiro,
    // para evitar que um nome curto quebre um nome maior que o contenha).
    let rebuilt = raw;
    const names = Object.keys(mentionUserMap).sort((a, b) => b.length - a.length);
    for (const name of names) {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      rebuilt = rebuilt.replace(
        new RegExp(`@${escaped}(?![\\w\\u00C0-\\u017F])`, 'g'),
        `@[${name}](${mentionUserMap[name]})`
      );
    }

    // Limite espelhado na RPC (5000) para evitar payloads abusivos
    const content = rebuilt.slice(0, 5000);
    const mentions = Array.from(
      new Set(
        Array.from(content.matchAll(/@\[[^\]]+\]\(([0-9a-fA-F-]{36})\)/g)).map((m) => m[1])
      )
    );
    setSendingInternalNote(true);
    try {
      await onSendInternalNote(content, mentions);
      setInputValue('');
      setShowUserMentions(false);
      setUserMentionQuery('');
      setUserMentionAnchor(null);
      setMentionUserMap({});
    } finally {
      setSendingInternalNote(false);
    }
  };


  const handleSend = () => {
    if (!inputValue.trim()) return;

    // Modo "Mensagem privada": desvia para a nota interna (nunca vai ao provider)
    if (composerMode === 'internal') {
      void handleSendInternalNote();
      return;
    }


    
    // Se está respondendo, passa o external_id e providerMessageId (para Notificame)
    const providerMsgId = (replyingTo?.metadata as { providerMessageId?: string })?.providerMessageId;
    
    // NOVA LÓGICA: Determina tipo de resposta baseado na mensagem sendo respondida
    let commentOptions: { reply_mode: 'comment_public'; comment_id: string } | undefined = undefined;
    
    if (isInstagramInbox && replyingTo) {
      // Verifica se está respondendo a um comentário do Instagram
      if (isCommentMessage(replyingTo.id)) {
        // Busca o comment_id do metadata da mensagem sendo respondida
        const commentId = (replyingTo.metadata as { comment_id?: string })?.comment_id;
        if (commentId) {
          commentOptions = {
            reply_mode: 'comment_public',
            comment_id: commentId,
          };
        }
      }
      // Se não é comentário, será enviado como Direct (comportamento padrão)
    }
    // Se não está respondendo nada: Direct (comportamento padrão)

    // ===== @MENÇÕES (somente grupos) =====
    // Cada @Nome ainda presente no texto vira "@<phone>" no payload enviado ao
    // provider (formato esperado por WhatsApp/WAHA/Z-API). O texto exibido
    // na UI permanece com "@Nome" (resolvido pelo render).
    let wireText = inputValue.trim();
    const activeMentions: Array<{ phone: string; name: string }> = [];
    if (conversation?.group_id && selectedMentions.size > 0) {
      selectedMentions.forEach((name, phone) => {
        const token = `@${name}`;
        if (wireText.includes(token)) {
          activeMentions.push({ phone, name });
          // Substitui todas as ocorrências do nome pelo telefone (formato wire)
          wireText = wireText.split(token).join(`@${phone}`);
        }
      });
    }

    onSendMessage(
      wireText,
      replyingTo?.external_id || undefined,
      providerMsgId,
      commentOptions,
      activeMentions.length > 0 ? activeMentions : undefined
    );
    setInputValue('');
    setReplyingTo(null);
    setSelectedMentions(new Map());
    setShowMentions(false);
    setMentionQuery('');
    setMentionAnchor(null);
  };

  // Handler para iniciar reply de uma mensagem
  const handleReply = (message: Message) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  };

  // Handler para scroll até uma mensagem específica (ao clicar em quoted)
  const scrollToMessage = (messageId: string) => {
    const element = messageRefs.current.get(messageId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight temporário
      element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
      }, 1500);
    }
  };

  // ===========================================================================
  // Scroll pendente do jump-to-message (pesquisa contextual)
  // Tenta rolar até a mensagem âncora após o React commitar a nova janela.
  // Re-tenta por até ~30 frames (~500ms) caso o ref ainda não tenha sido
  // montado (mensagens podem renderizar virtualizadas/condicionalmente).
  // ===========================================================================
  useEffect(() => {
    const target = pendingScrollTargetRef.current;
    if (!target) return;
    const el = messageRefs.current.get(target);
    if (el) {
      scrollToMessage(target);
      pendingScrollTargetRef.current = null;
      pendingScrollAttemptsRef.current = 0;
      return;
    }
    // Ainda não montou: agenda nova tentativa no próximo frame, com teto
    if (pendingScrollAttemptsRef.current >= 30) {
      logger.warn('ChatArea', `pendingScrollTarget não montado após 30 tentativas: ${target}`);
      pendingScrollTargetRef.current = null;
      pendingScrollAttemptsRef.current = 0;
      return;
    }
    pendingScrollAttemptsRef.current += 1;
    const raf = requestAnimationFrame(() => {
      // Força re-execução do efeito pegando o ref atualizado
      const el2 = messageRefs.current.get(target);
      if (el2) {
        scrollToMessage(target);
        pendingScrollTargetRef.current = null;
        pendingScrollAttemptsRef.current = 0;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [messages]);


  const handleKeyDown = (e: React.KeyboardEvent) => {
    // SHIFT + ENTER sempre insere quebra de linha (prioridade máxima)
    // Permite ao usuário formatar texto em múltiplas linhas como no WhatsApp Web
    if (e.key === 'Enter' && e.shiftKey) {
      // Não previne o comportamento padrão - deixa inserir a quebra de linha
      return;
    }

    // Se popover de ações rápidas está aberto
    if (showQuickActions) {
      // Escape fecha o popover e limpa input
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowQuickActions(false);
        setQuickActionSearch('');
        setInputValue('');
        return;
      }
      // Não envia mensagem se popover está aberto (deixa Command tratar)
      if (e.key === 'Enter') {
        return; // Deixa o CommandItem tratar o Enter
      }
      return;
    }
    
    // Comportamento padrão: Enter (sem Shift) envia mensagem
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatMessageTime = (dateStr: string) => {
    return format(new Date(dateStr), 'HH:mm', { locale: ptBR });
  };

  // Formata data da mensagem com tratamento especial para "Hoje", "Ontem"
  const formatMessageDate = (dateStr: string) => {
    const messageDate = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Se for hoje
    if (isSameDay(messageDate, today)) {
      return 'Hoje';
    }
    
    // Se for ontem
    if (isSameDay(messageDate, yesterday)) {
      return 'Ontem';
    }
    
    // Se for do mesmo ano, mostra dia e mês
    if (messageDate.getFullYear() === today.getFullYear()) {
      return format(messageDate, "dd 'de' MMMM", { locale: ptBR });
    }
    
    // Se for de outro ano, inclui o ano
    return format(messageDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  // Group messages by date
  const groupedMessages = messages.reduce<Record<string, Message[]>>((acc, msg) => {
    const date = format(new Date(msg.sent_at), 'yyyy-MM-dd');
    if (!acc[date]) acc[date] = [];
    acc[date].push(msg);
    return acc;
  }, {});

  // Handler quando usuário seleciona imagens para enviar
  const handleImagesSelect = (files: File[]) => {
    setSelectedImageFiles(files);
    setShowImageDialog(true);
  };

  // Handler para fechar dialog de imagem
  const handleCloseImageDialog = () => {
    setShowImageDialog(false);
    setSelectedImageFiles([]);
  };

  // Handler para abrir lightbox (modo single ou galeria)
  const handleOpenLightbox = (url: string, caption?: string | null) => {
    setLightboxData({ images: [url], initialIndex: 0, caption });
  };

  // Handler para abrir galeria de imagens
  const handleOpenGallery = (urls: string[], startIndex: number, caption?: string | null) => {
    setLightboxData({ images: urls, initialIndex: startIndex, caption });
  };

  // Handler quando usuário seleciona um documento para enviar
  const handleDocumentSelect = (file: File) => {
    setSelectedDocumentFile(file);
    setShowDocumentDialog(true);
  };

  // Handler para fechar dialog de documento
  const handleCloseDocumentDialog = () => {
    setShowDocumentDialog(false);
    setSelectedDocumentFile(null);
  };

  // Handler quando usuário seleciona um vídeo para enviar
  const handleVideoSelect = (file: File) => {
    setSelectedVideoFile(file);
    setShowVideoDialog(true);
  };

  // Handler para fechar dialog de vídeo
  const handleCloseVideoDialog = () => {
    setShowVideoDialog(false);
    setSelectedVideoFile(null);
  };

  // ===== DRAG-AND-DROP DE ARQUIVOS =====
  // Constantes de validação (mesmas do AttachmentButton)
  const MAX_IMAGES = 30;
  const MAX_IMAGE_SIZE = 16 * 1024 * 1024; // 16MB
  const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024; // 100MB

  /**
   * Handler para quando arquivo entra na área de drop
   * Mostra overlay visual indicando zona de drop
   */
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  /**
   * Handler para quando arquivo sai da área de drop
   * Esconde overlay apenas se realmente saiu (não é elemento filho)
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Só esconde se saiu realmente da área (não é elemento filho)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  /**
   * Handler para dragover - necessário para permitir drop
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Handler para quando arquivo é solto na área
   * Separa imagens de documentos e abre dialog apropriado
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    // Verifica se input está desabilitado
    const isClosed = conversation?.status === 'closed';
    const isAssigned = !!conversation?.assigned_to;
    const isAssignedToMe = conversation?.assigned_to === currentUserId;
    const isRestrictedInbox = restrictedInboxIds?.includes(conversation?.inbox_id || '');
    const isAgentWithoutAssignment = !isAdminOrSupervisor && !isAssignedToMe && !isRestrictedInbox;
    const hasPendingTransferWaiting = hasPendingTransfer && !isAssignedToMe;
    const isInputDisabledForDrop = isClosed || isAgentWithoutAssignment || isObserverModeActive || hasPendingTransferWaiting || !canSendInGroup;
    
    if (isInputDisabledForDrop) {
      toast.error('Não é possível enviar arquivos neste momento');
      return;
    }
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    
    // Separa imagens de documentos
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const documentFiles = files.filter(f => !f.type.startsWith('image/'));
    
    // Prioriza imagens se houver
    if (imageFiles.length > 0) {
      processDroppedImages(imageFiles);
    } else if (documentFiles.length > 0) {
      processDroppedDocument(documentFiles[0]);
    }
  };

  /**
   * Processa imagens arrastadas usando mesma validação do AttachmentButton
   */
  const processDroppedImages = (files: File[]) => {
    // Valida quantidade máxima (30 imagens) - MESMA REGRA DO ATTACHMENT
    if (files.length > MAX_IMAGES) {
      toast.error(`Máximo de ${MAX_IMAGES} imagens por envio`);
      return;
    }
    
    // Filtra apenas imagens válidas - MESMA LÓGICA DO ATTACHMENT
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file, index) => {
      if (!file.type.startsWith('image/')) {
        errors.push(`Arquivo ${index + 1}: não é uma imagem`);
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        errors.push(`${file.name}: maior que 16MB`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      toast.error(errors.slice(0, 3).join('\n') + 
        (errors.length > 3 ? `\n...e mais ${errors.length - 3} erros` : ''));
    }

    // Abre dialog existente com arquivos válidos
    if (validFiles.length > 0) {
      setSelectedImageFiles(validFiles);
      setShowImageDialog(true);
    }
  };

  /**
   * Processa documento arrastado usando mesma validação do AttachmentButton
   */
  const processDroppedDocument = (file: File) => {
    // Valida tamanho (100MB) - MESMA REGRA DO ATTACHMENT
    if (file.size > MAX_DOCUMENT_SIZE) {
      toast.error('Documento muito grande. O limite é 100MB.');
      return;
    }
    
    // Abre dialog existente
    setSelectedDocumentFile(file);
    setShowDocumentDialog(true);
  };

  // Renderiza o conteúdo da mensagem baseado no tipo
  const renderMessageContent = (message: Message) => {
    // Mensagens de imagem (suporta múltiplas via grid)
    if (message.message_type === 'image' && message.attachments && message.attachments.length > 0) {
      // Se tem mais de 1 imagem, renderiza grid
      if (message.attachments.length > 1) {
        const imageUrls = message.attachments.map(att => att.url);
        return (
          <ImageGridBubble
            attachments={message.attachments}
            caption={message.content}
            direction={message.direction}
            onOpenGallery={(startIndex) => handleOpenGallery(imageUrls, startIndex, message.content)}
          />
        );
      }
      
      // Imagem única
      const attachment = message.attachments[0];
      const metadata = attachment.metadata as { thumbnailUrl?: string } | null;
      return (
        <ImageMessageBubble
          url={attachment.url}
          caption={message.content}
          thumbnailUrl={metadata?.thumbnailUrl}
          direction={message.direction}
          onOpenLightbox={() => handleOpenLightbox(attachment.url, message.content)}
        />
      );
    }

    // Mensagens de áudio
    if (message.message_type === 'audio' && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      const metadata = attachment.metadata as { seconds?: number; ptt?: boolean } | null;
      
      // Verifica se áudio foi reproduzido pelo destinatário (metadata.played_at da Z-API)
      const messageMetadata = message.metadata as { played_at?: string } | null;
      const isAudioPlayed = !!messageMetadata?.played_at;
      
      return (
        <AudioMessageBubble
          url={attachment.url}
          duration={metadata?.seconds}
          isPTT={metadata?.ptt !== false}
          direction={message.direction}
          isAudioPlayed={isAudioPlayed}
        />
      );
    }

    // Mensagens de sticker
    if (message.message_type === 'sticker' && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      return (
        <img
          src={getStorageUrl(attachment.url) || attachment.url}
          alt="Sticker"
          className="max-w-[150px] max-h-[150px] object-contain"
        />
      );
    }

    // Mensagens de vídeo (normal e PTV circular)
    // Suporta tanto 'video' quanto 'ptv' como message_type
    if ((message.message_type === 'video' || message.message_type === 'ptv') && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      const metadata = attachment.metadata as { 
        seconds?: number; 
        isPtv?: boolean; 
        thumbnailUrl?: string 
      } | null;
      
      // Se message_type é 'ptv', força isPtv=true mesmo que metadata não tenha
      const isPtv = message.message_type === 'ptv' || metadata?.isPtv;
      
      return (
        <VideoMessageBubble
          url={attachment.url}
          caption={message.content}
          duration={metadata?.seconds}
          isPtv={isPtv}
          thumbnailUrl={metadata?.thumbnailUrl}
          direction={message.direction}
        />
      );
    }

    // Mensagens de documento
    if (message.message_type === 'document' && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      return (
        <DocumentMessageBubble
          url={attachment.url}
          filename={attachment.filename}
          mimeType={attachment.mime_type}
          sizeBytes={attachment.size_bytes}
          direction={message.direction}
          caption={message.content}
        />
      );
    }

    // Fallback para documentos que falharam no upload (sem attachment)
    // Ocorre quando o arquivo não pôde ser salvo no storage (MIME type não suportado, etc.)
    if (message.message_type === 'document' && (!message.attachments || message.attachments.length === 0)) {
      return (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-dashed border-muted-foreground/30">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
            <FileIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">
              {message.content || 'Documento não disponível'}
            </span>
            <span className="text-xs text-muted-foreground/70">
              Arquivo não pôde ser carregado
            </span>
          </div>
        </div>
      );
    }

    // Mensagens de localização
    if (message.message_type === 'location' && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      const metadata = attachment.metadata as {
        latitude?: number;
        longitude?: number;
        name?: string;
        address?: string;
      } | null;
      
      return (
        <LocationMessageBubble
          latitude={metadata?.latitude || 0}
          longitude={metadata?.longitude || 0}
          name={metadata?.name}
          address={metadata?.address}
          direction={message.direction}
        />
      );
    }

    // Mensagens de contato compartilhado
    if ((message.message_type as string) === 'contact' && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      const metadata = attachment.metadata as {
        displayName?: string;
        vcard?: string;
        phone?: string;
        businessDescription?: string;
      } | null;
      
      return (
        <ContactMessageBubble
          displayName={metadata?.displayName || message.content || 'Contato'}
          phone={metadata?.phone}
          businessDescription={metadata?.businessDescription}
          vcard={metadata?.vcard}
          direction={message.direction}
          onStartConversation={onStartConversationWithContact}
        />
      );
    }

    // =========================================================================
    // Mensagens de botão PIX (chave copiável estilo WhatsApp)
    // =========================================================================
    if (message.message_type === 'button_pix' && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      const metadata = attachment.metadata as {
        name?: string;
        pixKey?: string;
        keyType?: 'CPF' | 'CNPJ' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
        message?: string;
      } | null;
      
      return (
        <PixButtonMessageBubble
          name={metadata?.name || 'PIX'}
          pixKey={metadata?.pixKey || ''}
          keyType={metadata?.keyType || 'ALEATORIA'}
          message={metadata?.message}
          isOutbound={message.direction === 'outbound'}
        />
      );
    }

    // Mensagens de botões interativos (button_list)
    if (message.message_type === 'button_list') {
      const metadata = message.metadata as { question_options?: Array<{id: string; label: string}> } | null;
      return (
        <ButtonListMessageBubble
          content={message.content || ''}
          options={metadata?.question_options || []}
          direction={message.direction}
        />
      );
    }

    // Mensagens de imagem com botões interativos (image_buttons - usado em campanhas)
    if (message.message_type === 'image_buttons') {
      const metadata = message.metadata as { 
        question_options?: Array<{id: string; label: string}>;
        media_url?: string;
      } | null;
      const attachment = message.attachments?.[0];
      const imageUrl = attachment?.url || metadata?.media_url || '';
      
      return (
        <ImageButtonsMessageBubble
          imageUrl={imageUrl}
          content={message.content || ''}
          buttons={metadata?.question_options || []}
          direction={message.direction}
          onOpenLightbox={() => {
            if (imageUrl) {
              const fullUrl = getStorageUrl(imageUrl) || imageUrl;
              setLightboxData({ images: [fullUrl], initialIndex: 0, caption: message.content });
            }
          }}
        />
      );
    }

    // Mensagens de lista interativa (option_list)
    if (message.message_type === 'option_list') {
      const metadata = message.metadata as { 
        question_options?: Array<{id: string; label: string; description?: string}>;
        title?: string;
      } | null;
      return (
        <OptionListMessageBubble
          content={message.content || ''}
          title={metadata?.title}
          options={metadata?.question_options || []}
          direction={message.direction}
        />
      );
    }

    // ========== MENSAGENS DE CARROSSEL INTERATIVO ==========
    // Cartões deslizantes estilo WhatsApp com imagem, texto e botões
    if (message.message_type === 'carousel' && message.attachments && message.attachments.length > 0) {
      // Filtra apenas attachments do tipo carousel_card e ordena por index
      const carouselCards = message.attachments
        .filter(att => att.type === 'carousel_card')
        .sort((a, b) => {
          const indexA = (a.metadata as { index?: number })?.index ?? 0;
          const indexB = (b.metadata as { index?: number })?.index ?? 0;
          return indexA - indexB;
        })
        .map(att => {
          const metadata = att.metadata as {
            index?: number;
            text?: string;
            buttons?: Array<{ type: string; label: string; url?: string; phone?: string }>;
          };
          return {
            index: metadata?.index ?? 0,
            imageUrl: att.url,
            text: metadata?.text || '',
            buttons: metadata?.buttons || [],
          };
        });

      if (carouselCards.length > 0) {
        return (
          <CarouselMessageBubble
            title={message.content || ''}
            cards={carouselCards}
            direction={message.direction}
            onOpenGallery={(cardIndex) => {
              const imageUrls = carouselCards.map(c => c.imageUrl);
              handleOpenGallery(imageUrls, cardIndex, message.content);
            }}
          />
        );
      }
    }

    // Mensagens de link com preview (tipo não está no enum, verificação via string)
    if ((message.message_type as string) === 'link' && message.attachments && message.attachments.length > 0) {
      const attachment = message.attachments[0];
      const metadata = attachment.metadata as {
        linkUrl?: string;
        title?: string;
        description?: string;
        thumbnailUrl?: string;
      } | null;
      
      return (
        <LinkMessageBubble
          url={metadata?.linkUrl || attachment.url}
          title={metadata?.title}
          description={metadata?.description}
          thumbnailUrl={metadata?.thumbnailUrl}
          message={message.content}
          direction={message.direction}
        />
      );
    }

    // Mensagens de texto que contêm URLs - renderizar como link com preview
    if (message.message_type === 'text' && message.content) {
      const url = extractUrlFromText(message.content);
      if (url) {
        const videoId = extractYouTubeVideoId(url);
        const thumbnailUrl = videoId ? getYouTubeThumbnail(videoId) : undefined;
        const title = videoId ? 'Clique para assistir no YouTube' : undefined;
        
        return (
          <LinkMessageBubble
            url={url}
            title={title}
            thumbnailUrl={thumbnailUrl}
            message={message.content !== url ? message.content : undefined}
            direction={message.direction}
          />
        );
      }
    }

    // ========== MENSAGENS DE CHAMADA (call_log) ==========
    // Notificações de chamadas perdidas/recebidas do WhatsApp
    if (message.message_type === 'call_log') {
      const metadata = message.metadata as {
        call_type?: 'voice' | 'video';
        call_status?: 'missed' | 'received' | 'rejected';
        duration_seconds?: number;
      } | null;
      
      return (
        <MissedCallMessageBubble
          callType={metadata?.call_type || 'voice'}
          callStatus={metadata?.call_status || 'missed'}
          direction={message.direction}
          duration={metadata?.duration_seconds}
          time={format(new Date(message.sent_at), 'HH:mm')}
        />
      );
    }

    // Mensagens de template (WhatsApp Business API)
    if (message.message_type === 'template') {
      const metadata = message.metadata as {
        template_name?: string;
        template_body?: string;
        template_params?: Array<{ type: string; text?: string }>;
        // Dados de botões para visualização
        template_buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
        button_params?: Array<{
          type: 'button';
          sub_type: 'url';
          index: number;
          parameters: Array<{ type: 'text'; text: string }>;
        }>;
        order_details_params?: {
          pix_code: string;
          merchant_name: string;
          pix_key: string;
          pix_key_type: string;
          amount_value: number;
          item_name?: string;
        };
        // Header de mídia do template
        header_format?: string;
        header_media_url?: string;
      } | null;
      
      return (
        <TemplateMessageBubble
          templateName={metadata?.template_name || 'Template'}
          templateBody={metadata?.template_body || message.content || ''}
          direction={message.direction}
          buttons={metadata?.template_buttons}
          buttonParams={metadata?.button_params}
          orderDetailsParams={metadata?.order_details_params}
          headerFormat={metadata?.header_format}
          headerMediaUrl={metadata?.header_media_url}
        />
      );
    }

    // ========== MENSAGENS DE BOTAO LINK (CTA URL) ==========
    // Mensagens enviadas pelo node button_link do flow builder
    if ((message.message_type as string) === 'button_link') {
      const metadata = message.metadata as {
        button_label?: string;
        link_url?: string;
        title?: string;
        footer?: string;
      } | null;

      const buttonLabel = metadata?.button_label || 'Acessar';
      const linkUrl = metadata?.link_url || '';
      const isOutbound = message.direction === 'outbound';

      return (
        <div className="flex flex-col gap-1.5">
          {/* Titulo opcional (header) */}
          {metadata?.title && (
            <p 
              className={cn("text-sm font-bold whitespace-pre-wrap break-all", !isOutbound && "text-foreground")}
              style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
            >
              {metadata.title}
            </p>
          )}
          {/* Texto da mensagem (com resolução de @menções em grupos) */}
          <p className="text-sm whitespace-pre-wrap break-all">
            {renderTextWithMentions(message.content)}
          </p>
          {/* Footer opcional */}
          {metadata?.footer && (
            <p 
              className={cn("text-xs", !isOutbound && "text-muted-foreground")}
              style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
            >
              {metadata.footer}
            </p>
          )}
          {/* Botao CTA - estilo WhatsApp */}
          {linkUrl && (
            <div 
              className="mt-2 pt-2 border-t"
              style={{ borderColor: isOutbound ? 'hsl(var(--chat-text-secondary) / 0.3)' : 'hsl(var(--chat-border))' }}
            >
              <a 
                href={linkUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm transition-opacity hover:opacity-80"
                style={{ color: 'hsl(200 98% 36%)' }}
              >
                <ExternalLink className="h-4 w-4" />
                <span className="font-medium">{buttonLabel}</span>
              </a>
            </div>
          )}
        </div>
      );
    }

    // Mensagens de texto (padrão) - sem URL
    // Em conversas de grupo, resolve "@<lid|telefone>" para "@Nome do contato".
    return (
      <p className="text-sm whitespace-pre-wrap break-all">
        {renderTextWithMentions(message.content)}
      </p>
    );
  };

  if (!conversation) {
    return (
      <div 
        className="flex-1 flex flex-col items-center justify-center rounded-lg shadow-sm border"
        style={{ backgroundColor: theme === 'dark' ? '#0b141a' : '#f0ebe3' }}
      >
        {/* Ilustração estilo WhatsApp Business - cores condicionais para modo noturno */}
        <div className="mb-8">
          <svg width="260" height="180" viewBox="0 0 260 180" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Laptop base */}
            <rect x="30" y="40" width="140" height="90" rx="8" fill={theme === 'dark' ? '#2a3942' : '#d4cfc6'} />
            <rect x="38" y="48" width="124" height="70" rx="4" fill={theme === 'dark' ? '#111b21' : '#ffffff'} />
            {/* Laptop keyboard */}
            <rect x="20" y="130" width="160" height="10" rx="2" fill={theme === 'dark' ? '#233138' : '#c9c4bb'} />
            
            {/* Chat bubbles no laptop */}
            <rect x="50" y="58" width="50" height="12" rx="6" fill={theme === 'dark' ? '#005c4b' : '#d9fdd3'} />
            <rect x="80" y="75" width="60" height="12" rx="6" fill={theme === 'dark' ? '#1f2c33' : '#ffffff'} stroke={theme === 'dark' ? '#2a3942' : '#e0ddd5'} strokeWidth="1" />
            <rect x="50" y="92" width="45" height="12" rx="6" fill={theme === 'dark' ? '#005c4b' : '#d9fdd3'} />
            
            {/* Phone */}
            <rect x="160" y="50" width="60" height="100" rx="8" fill={theme === 'dark' ? '#2a3942' : '#d4cfc6'} />
            <rect x="166" y="60" width="48" height="80" rx="4" fill={theme === 'dark' ? '#111b21' : '#ffffff'} />
            
            {/* Chat bubbles no phone */}
            <rect x="172" y="70" width="30" height="8" rx="4" fill={theme === 'dark' ? '#005c4b' : '#d9fdd3'} />
            <rect x="180" y="82" width="32" height="8" rx="4" fill={theme === 'dark' ? '#1f2c33' : '#ffffff'} stroke={theme === 'dark' ? '#2a3942' : '#e0ddd5'} strokeWidth="1" />
            <rect x="172" y="94" width="25" height="8" rx="4" fill={theme === 'dark' ? '#005c4b' : '#d9fdd3'} />
            
            {/* Connection arrow */}
            <path d="M135 85 L158 75" stroke="#25d366" strokeWidth="3" strokeLinecap="round" />
            <path d="M152 70 L158 75 L153 81" stroke="#25d366" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            
            {/* Checkmark success */}
            <circle cx="220" cy="45" r="16" fill="#25d366" />
            <path d="M212 45 L218 51 L228 39" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            
            {/* Decorative hearts */}
            <path d="M230 100 C230 96 235 93 238 96 C241 93 246 96 246 100 C246 105 238 112 238 112 C238 112 230 105 230 100" fill="#25d366" opacity="0.6" />
            <path d="M210 120 C210 117 213 115 215 117 C217 115 220 117 220 120 C220 123 215 128 215 128 C215 128 210 123 210 120" fill="#25d366" opacity="0.4" />
          </svg>
        </div>
        
        <h3 className="text-2xl font-light mb-3" style={{ color: theme === 'dark' ? '#e9edef' : '#41525d' }}>
          Atendimento Uaizy Chat
        </h3>
        <p className="text-center max-w-md text-sm" style={{ color: theme === 'dark' ? '#8696a0' : '#667781' }}>
          Selecione uma conversa na lista ao lado para começar a visualizar e responder mensagens.
        </p>
        
        {/* Footer com ícone de segurança */}
        <div className="absolute bottom-8 flex items-center gap-2" style={{ color: '#8696a0' }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 1L2 3v3c0 2.5 1.7 4.8 4 5.5 2.3-.7 4-3 4-5.5V3L6 1zm0 5.5h3c-.2 1.8-1.4 3.3-3 3.9V6.5H3V3.7l3-1.5v4.3z"/>
          </svg>
          <span className="text-xs">CRM mais completo do mercado, tudo em um só lugar.</span>
        </div>
      </div>
    );
  }

  const isClosed = conversation.status === 'closed';
  
  // ===== CONTROLE DE INPUT PARA AGENTES =====
  // 
  // Regras para agente poder enviar mensagem:
  // 
  // 1. INBOX RESTRITA (access_restricted=true + agente em inbox_agents):
  //    → Pode conversar livremente SEM assumir, pois a inbox é "particular" dele
  //    → Caso de uso: Vendedor com WhatsApp próprio na empresa
  // 
  // 2. INBOX PÚBLICA - Agente é assigned_to:
  //    → Pode conversar normalmente
  // 
  // 3. INBOX PÚBLICA - Agente NÃO é assigned_to:
  //    → Precisa clicar em "Assumir" para iniciar atendimento
  //
  // Admins e supervisores sempre podem conversar em qualquer conversa
  
  // Verifica se agente está em inbox restrita (já é "dono" da inbox)
  const isAgentFromRestrictedInbox = restrictedInboxIds?.includes(conversation.inbox_id) ?? false;
  
  // Verifica se é agente sem atribuição da conversa (precisa assumir)
  // EXCEÇÃO: Se a inbox é restrita e agente é dono, NÃO precisa assumir
  const isAgentWithoutAssignment = !isAdminOrSupervisor && 
    currentUserId && 
    conversation.assigned_to !== currentUserId &&
    !isAgentFromRestrictedInbox;
  
  // Verifica se existe transferência pendente aguardando "Iniciar"
  // Bloqueia input até que o agente clique em "Iniciar" para aceitar a transferência
  const hasPendingTransferWaiting = hasPendingTransfer && 
    currentUserId === conversation.assigned_to;
  
  // Verifica se está respondendo a um comentário do Instagram
  // Comentários NÃO possuem restrição de janela 24h — agente pode responder a qualquer momento
  const isReplyingToComment = isInstagramInbox && replyingTo && isCommentMessage(replyingTo.id);

  // Bloqueio de sessão expirada: não aplicar quando respondendo comentário do Instagram
  // nem quando o composer está em "Mensagem privada" (nota interna — nunca sai do sistema,
  // portanto a janela de 24h do provider não se aplica)
  const sessionExpiredBlock = isSessionWindowExpired && !isReplyingToComment && composerMode !== 'internal';

  // Desabilita input se fechado, sem permissão de grupo, sem atribuição, ou janela 24h expirada
  // Desabilita input também quando modo observador estiver ativo, transferência pendente
  // ou modo read-only (inbox sem permissão para o usuário - contexto CRM/Kanban)
  const isInputDisabled = isClosed || !canSendInGroup || isAgentWithoutAssignment || sessionExpiredBlock || isObserverModeActive || hasPendingTransferWaiting || isReadOnlyMode;

  return (
    <div 
      className="flex-1 h-full flex flex-col bg-card rounded-lg shadow-sm border overflow-hidden relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Overlay de Drop Zone - aparece quando arrastando arquivo */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/5 backdrop-blur-sm pointer-events-none">
          <div className="flex flex-col items-center gap-4 p-8 rounded-2xl border-2 border-dashed border-primary bg-background/90 shadow-lg">
            <div className="p-4 rounded-full bg-primary/10">
              <Paperclip className="h-12 w-12 text-primary" />
            </div>
            <p className="text-lg font-medium text-foreground">
              Solte para enviar arquivo
            </p>
            <p className="text-sm text-muted-foreground">
              Imagens (máx. 16MB) ou Documentos (máx. 100MB)
            </p>
          </div>
        </div>
      )}
      {/* Header - Dinâmico para grupos ou contatos - Responsivo mobile */}
      {/* Em telas compactas (< 380px) reduzimos paddings/gaps para caber tudo em uma linha */}
      <div className="h-12 md:h-16 px-1 sm:px-2 md:px-4 flex items-center justify-between gap-1 bg-card rounded-t-lg">
        <div className="flex items-center gap-1 md:gap-3 flex-1 min-w-0">
          {/* Botão voltar - visível apenas em mobile */}
          {onBackToList && (
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-7 w-7 shrink-0"
              onClick={onBackToList}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          {conversation.group_id ? (
            /* Avatar de grupo - sem lightbox */
            <Avatar className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 shrink-0">
              <AvatarImage src={getStorageUrl(conversation.group?.avatar_url)} />
              <AvatarFallback className="bg-blue-100 text-blue-600">
                <Users className="h-4 w-4 md:h-5 md:w-5" />
              </AvatarFallback>
            </Avatar>
          ) : (
            /* Avatar do contato - com lightbox para visualização ampliada */
            <AvatarWithLightbox
              src={conversation.contact?.avatar_url}
              name={conversation.contact?.name}
              email={conversation.contact?.phone}
              className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 shrink-0"
              size="md"
            />
          )}
                {/* Bloco de nome/status: ocupa o espaço restante (flex-1 + min-w-0)
                    para nunca sobrepor as ações à direita no mobile. */}
                <div className="flex-1 min-w-0 md:max-w-[280px] overflow-hidden">

                  <h3 
                    className="font-medium text-sm md:text-base truncate overflow-hidden whitespace-nowrap" 
                    title={conversation.group_id 
                      ? conversation.group?.name || 'Grupo' 
                      : conversation.contact?.name || conversation.contact?.phone || 'Contato'}
                  >
                    {conversation.group_id 
                      ? conversation.group?.name || 'Grupo' 
                      : conversation.contact?.name || conversation.contact?.phone || 'Contato'}
                  </h3>
            {/* Typing/Recording indicator para contatos, contador de participantes para grupos */}
            {conversation.group_id ? (
              <span className="text-[10px] md:text-xs text-muted-foreground">
                {conversation.group?.participant_count || 0} participantes
              </span>
            ) : contactPresence?.isTyping ? (
              <span className="text-[10px] md:text-xs text-primary animate-pulse">
                Digitando...
              </span>
            ) : contactPresence?.isRecording ? (
              <span className="text-[10px] md:text-xs text-primary animate-pulse">
                🎙️ Gravando áudio...
              </span>
            ) : isCompact ? (
              // Viewport compacto (< 380px): badges ocultos para o nome não ser espremido.
              // As mesmas informações continuam disponíveis na sidebar do contato.
              null
            ) : (
              // Linha de badges: uma única linha com truncamento (sem wrap)
              <div className="flex items-center gap-1 md:gap-1.5 flex-nowrap min-w-0 overflow-hidden">


                {/* Badge da Inbox - oculto quando sidebar direita está aberta
                    para evitar sobreposição com nome do contato/status em viewports estreitos. */}
                {!showSidebar && (
                  <Badge
                    variant="outline"
                    className="text-[9px] md:text-[10px] px-1 md:px-1.5 py-0 h-3.5 md:h-4 font-medium min-w-0 max-w-[90px] sm:max-w-[140px] shrink truncate block"
                    title={conversation.inbox.name}
                  >
                    {conversation.inbox.name}
                  </Badge>
                )}
                {/* Badge de Status - layout compacto */}
                <Badge variant="secondary" className="text-[9px] md:text-[10px] px-1 md:px-1.5 py-0 h-3.5 md:h-4 gap-0.5 shrink-0">
                  {statusIcons[conversation.status]}
                  <span className="hidden sm:inline">{statusLabels[conversation.status]}</span>
                </Badge>
                {/* Etiquetas da conversa - apenas se provider suporta */}
                {labelsSupported && conversationLabels.length > 0 && (
                  <span className="hidden md:inline-flex">

                    <LabelBadgeList 
                      labels={conversationLabels.map(cl => cl.label)} 
                      size="sm" 
                      maxVisible={2} 
                    />
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ===== AÇÕES DE CONVERSA (Assumir / Iniciar / Encerrar) =====
              Posicionados à esquerda, logo após os badges de status, para
              afastar dos ícones de atalho do header (lado direito). */}
          {/* Em viewport compacto estas ações migram para o menu de 3 pontinhos */}
          <div className={cn('items-center gap-1 md:gap-1.5 shrink-0', isCompact ? 'hidden' : 'flex')}>
            {/* Botão Assumir Conversa - aparece apenas para conversas na fila (pending + sem responsável) */}
            {conversation.status === 'pending' &&
             conversation.assigned_to === null &&
             onClaimConversation && (
              <Button
                size="sm"
                onClick={onClaimConversation}
                className="gap-0.5 h-6 px-2 text-[10px] bg-green-600 hover:bg-green-700 text-white"
              >
                <UserCheck className="h-2.5 w-2.5" />
                Assumir
              </Button>
            )}

            {/* Botão Iniciar Atendimento - aparece quando existe transferência pendente para o usuário atual */}
            {!isReadOnlyMode && hasPendingTransferWaiting && onStartConversation && (
              <Button
                size="sm"
                onClick={onStartConversation}
                disabled={isStartingConversation}
                className="gap-1 h-6 px-2 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isStartingConversation ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <PlayCircle className="h-3 w-3" />
                )}
                Iniciar
              </Button>
            )}

            {/* Botão Encerrar Conversa - aparece quando conversa está aberta e sem transferência pendente */}
            {!isReadOnlyMode &&
             conversation.status === 'open' &&
             !hasPendingTransferWaiting &&
             onCloseConversation && (
              <Button
                size="sm"
                variant="outline"
                onClick={onCloseConversation}
                className="gap-1 h-6 px-2 text-xs text-destructive hover:bg-destructive/10 border-destructive/30"
              >
                <XCircle className="h-3 w-3" />
                Encerrar
              </Button>
            )}
          </div>
        </div>

        {/* data-chat-header-actions: permite que contêineres externos (ex.: modal do
            Kanban) reservem espaço para o botão de fechar do Dialog sem sobrepor */}
        <div data-chat-header-actions className="flex items-center gap-0.5 md:gap-2 shrink-0">
          {/* Toggle Modo Observador movido para área inferior do chat (abaixo do input) */}

          {/* ===== AÇÕES PRINCIPAIS EM MODO COMPACTO (somente ícone) =====
              Em telas < 380px os botões com texto sobrepunham os ícones do header,
              então viram ícones aqui e "Encerrar" migra para o menu de 3 pontinhos. */}
          {isCompact && conversation.status === 'pending' &&
           conversation.assigned_to === null && onClaimConversation && (
            <Button
              size="icon"
              onClick={onClaimConversation}
              aria-label="Assumir conversa"
              title="Assumir conversa"
              className="h-7 w-7 bg-green-600 hover:bg-green-700 text-white"
            >
              <UserCheck className="h-3.5 w-3.5" />
            </Button>
          )}
          {isCompact && !isReadOnlyMode && hasPendingTransferWaiting && onStartConversation && (
            <Button
              size="icon"
              onClick={onStartConversation}
              disabled={isStartingConversation}
              aria-label="Iniciar atendimento"
              title="Iniciar atendimento"
              className="h-7 w-7 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isStartingConversation ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <PlayCircle className="h-3.5 w-3.5" />
              )}
            </Button>
          )}


          {/* ===== BARRA DE ATALHOS EM ÍCONES (HEADER) =====
              Padronização visual: bg-muted/50, h-8 w-8, rounded-lg.
              Ações de mutação são ocultas em modo somente-leitura. */}
          {!isReadOnlyMode && (
            <div className="flex items-center gap-1">
              {/* Atalho: Alterar Categoria / Fase do Kanban */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-muted/50 hover:bg-muted"
                    title="Alterar categoria / fase"
                    aria-label="Alterar categoria / fase"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72 p-3 space-y-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Categoria e fase
                  </p>
                  {conversation.contact && (
                    <>
                      <InlineCategoryPicker
                        conversationId={conversation.id}
                        contactId={conversation.contact.id}
                        inboxId={conversation.inbox.id}
                        currentPhase={headerKanbanPhase}
                        pendingCategoryId={headerPendingCategoryId}
                        onPendingCategoryChange={setHeaderPendingCategoryId}
                        onChanged={onRefreshConversation}
                      />
                      <InlinePhasePicker
                        conversationId={conversation.id}
                        contactId={conversation.contact.id}
                        inboxId={conversation.inbox.id}
                        currentPhase={headerKanbanPhase}
                        pendingCategoryId={headerPendingCategoryId}
                        onPendingCategoryChange={setHeaderPendingCategoryId}
                        onChanged={onRefreshConversation}
                      />
                    </>
                  )}
                </PopoverContent>
              </Popover>

              {/* v1.2.0: Atalho funcional para fixar/desfixar a conversa atual.
                  Persistido em conversation_pins (mantém entre dispositivos). */}
              <Button
                variant="ghost"
                size="icon"
                disabled={isPinning || !conversation}
                className={cn(
                  "hidden md:inline-flex h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted",
                  conversation?.is_pinned && "text-primary"
                )}
                title={conversation?.is_pinned ? 'Desfixar conversa' : 'Fixar conversa'}
                aria-label={conversation?.is_pinned ? 'Desfixar conversa' : 'Fixar conversa'}
                onClick={async () => {
                  if (!conversation) return;
                  try {
                    if (conversation.is_pinned) {
                      await onUnpinConversation?.(conversation.id);
                      toast.success('Conversa desfixada');
                    } else {
                      await onPinConversation?.(conversation.id);
                      toast.success('Conversa fixada no topo');
                    }
                  } catch (err) {
                    toast.error('Não foi possível atualizar a fixação');
                  }
                }}
              >
                <Pin className={cn("h-4 w-4", conversation?.is_pinned && "fill-current")} />
              </Button>

              {/* Atalho: Marcar como Pendente */}
              <Button
                variant="ghost"
                size="icon"
                className="hidden md:inline-flex h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted"
                title="Marcar como pendente"
                aria-label="Marcar como pendente"
                onClick={() => onUpdateStatus('pending')}
              >
                <Clock className="h-4 w-4" />
              </Button>


              {/* Atalho: Transferir para Agente */}
              <Button
                variant="ghost"
                size="icon"
                className="hidden md:inline-flex h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted"
                title="Transferir para agente"
                aria-label="Transferir para agente"
                onClick={() => setShowTransferDialog(true)}
              >
                <Users className="h-4 w-4" />
              </Button>

              {/* Atalho: Transferir para Departamento */}
              <Button
                variant="ghost"
                size="icon"
                className="hidden md:inline-flex h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted"
                title="Transferir para departamento"
                aria-label="Transferir para departamento"
                onClick={() => setShowTransferDeptDialog(true)}
              >
                <ArrowRightLeft className="h-4 w-4" />
              </Button>

              {/* Atalho: Etiquetas do provider — ocultado a pedido (mantido em código para referência futura) */}
              {false && labelsSupported && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:inline-flex h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted"
                  title="Gerenciar etiquetas"
                  aria-label="Gerenciar etiquetas"
                  onClick={() => setShowLabelsDialog(true)}
                >
                  <Tag className="h-4 w-4" />
                </Button>
              )}

              {/* Atalho: Tags do contato */}
              {conversation.contact && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:inline-flex h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted"
                  title="Tags do contato"
                  aria-label="Tags do contato"
                  onClick={() => setShowContactTagsDialog(true)}
                >
                  <Tags className="h-4 w-4" />
                </Button>
              )}

              {/* Atalho: Pesquisar na conversa (placeholder) */}
              <Button
                variant="ghost"
                size="icon"
                className="hidden md:inline-flex h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted"
                title="Pesquisar na conversa"
                aria-label="Pesquisar na conversa"
                onClick={() => setShowSearchSheet(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Botão de agendamento foi movido para o ChatQuickActionsFab (lateral esquerda) */}

          {/* ===== BOTÃO ACESSO REMOTO WEBRTC ===== */}
          {/* Apenas para tenant de suporte e conversas individuais */}
          {showRemoteAccessButton && (
            <ScreenShareButton
              sessionState={agentScreenShare.sessionState}
              onRequestScreenShare={agentScreenShare.requestScreenShare}
              onEndScreenShare={agentScreenShare.endSession}
              disabled={conversation.status !== 'open'}
            />
          )}

          {/* ===== MENU DROPDOWN DE AÇÕES (3 pontinhos) =====
              Mantido para ações de overflow / futuras. */}
          {!isReadOnlyMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Compacto: "Encerrar" sai do header e passa a viver aqui */}
                {isCompact && conversation.status === 'open' &&
                 !hasPendingTransferWaiting && onCloseConversation && (
                  <DropdownMenuItem
                    onClick={onCloseConversation}
                    className="text-destructive focus:text-destructive"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Encerrar Conversa
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onUpdateStatus('pending')}>
                  <Clock className="h-4 w-4 mr-2" />
                  Marcar como Pendente
                </DropdownMenuItem>

                {/* Mobile: atalhos do header que ficam ocultos em telas pequenas */}
                <DropdownMenuItem
                  className="md:hidden"
                  onClick={() => setShowTransferDialog(true)}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Transferir para Agente
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="md:hidden"
                  onClick={() => setShowTransferDeptDialog(true)}
                >
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Transferir para Departamento
                </DropdownMenuItem>
                {conversation.contact && (
                  <DropdownMenuItem
                    className="md:hidden"
                    onClick={() => setShowContactTagsDialog(true)}
                  >
                    <Tags className="h-4 w-4 mr-2" />
                    Tags do Contato
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="md:hidden"
                  onClick={() => setShowSearchSheet(true)}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Pesquisar na Conversa
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onUpdateStatus('spam')}
                  className="text-destructive focus:text-destructive"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Marcar como Spam
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Botão sidebar - visível em todas as telas */}
          <Button variant="ghost" size="icon" onClick={onToggleSidebar} className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="16" y2="12" />
              <line x1="4" y1="18" x2="12" y2="18" />
            </svg>
          </Button>
        </div>
      </div>
      

      {/* Tarja de destaque para anotações não lidas do contato */}
      <ContactNotesAlertBanner contactId={conversation.contact?.id} />

      {/* Tarja de destaque para mensagens agendadas pendentes da conversa */}
      <ScheduledMessagesAlertBanner
        conversationId={conversation.id}
        conversationContext={{
          inboxId: conversation?.inbox_id,
          inboxName: conversation?.inbox?.name,
          channelType: conversation?.inbox?.channel_type,
          lastInboundAt: (conversation as any)?.last_inbound_at ?? null,
        }}
      />

      {/* Banner de bot pausado movido para ContactSidebar - área de chat mais limpa */}
      
      {/* REMOVIDO: Preview da postagem no header - agora exibido inline no corpo do chat */}

      {/* ====================================================================== */}
      {/* Corpo do chat (fundo + scroll + overlay do input)                       */}
      {/* ====================================================================== */}
      {/*
        IMPORTANTE:
        - Este wrapper é o que garante que o fundo/pattern chegue até o final.
        - O ScrollArea fica “transparente” e rola por cima do fundo.
        - O input (e tudo que fica junto dele) vira overlay absoluto.
      */}
      <div
        className="relative flex-1 min-h-0 overflow-hidden"
        style={{
          backgroundColor: 'hsl(var(--chat-background))',
          /* Plano de fundo: imagem escura no modo noturno, whatsapp no modo claro */
          backgroundImage: theme === 'dark' ? `url(${getStorageUrl('system/branding_fundo_preto.png')})` : (() => {
            const url = getStorageUrl('system/background-whatsapp.png');
            return url ? `url(${url})` : undefined;
          })(),
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto',
        }}
      >
        {/* Messages - Responsivo com padding menor em mobile */}
        <ScrollArea
          className="h-full p-2 md:p-4"
          ref={(node) => {
            // Captura o viewport interno do Radix ScrollArea para podermos
            // preservar a posição visual ao paginar mensagens anteriores.
            scrollViewportRef.current =
              (node?.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement | null) ?? null;
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-muted-foreground">
                Nenhuma mensagem ainda. Inicie a conversa!
              </p>
            </div>
          ) : (
            <div
              className={cn(
                'space-y-4 overflow-hidden',
                // Recuo lateral quando o FAB está aberto, evitando sobreposição dos ícones
                fabOpen && 'pl-[51px]',
              )}
              // Padding dinâmico: garante que a última mensagem fique acima do input overlay
              style={{ paddingBottom: inputOverlayHeight + INPUT_OVERLAY_EXTRA_PADDING_PX }}
            >
            {/* Botão para carregar mensagens anteriores */}
            {hasMoreMessages && (
              <div className="flex justify-center py-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    // Snapshot da posição ANTES de carregar para preservar
                    // o ponto visual após o prepend das mensagens antigas.
                    const vp = scrollViewportRef.current;
                    if (vp) {
                      prevScrollHeightRef.current = vp.scrollHeight;
                      prevScrollTopRef.current = vp.scrollTop;
                    }
                    isLoadingOlderRef.current = true;
                    // Ativa modo histórico: novas mensagens não vão mais empurrar
                    // a viewport para o fim. Em vez disso, mostra pílula "X novas mensagens".
                    setIsHistoryBrowsingMode(true);
                    setHistoryNewMessagesCount(0);
                    onLoadMoreMessages?.();
                  }}
                  disabled={loadingMoreMessages}
                  className="gap-2"
                >
                  {loadingMoreMessages ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                  {loadingMoreMessages ? 'Carregando...' : 'Carregar mensagens anteriores'}
                </Button>
              </div>
            )}
            
            {/* Contador de mensagens exibidas */}
            {totalMessagesCount !== null && messages.length < totalMessagesCount && (
              <div className="text-xs text-muted-foreground text-center py-1">
                Exibindo {messages.length} de {totalMessagesCount.toLocaleString('pt-BR')} mensagens
              </div>
            )}
            
            {Object.entries(groupedMessages).map(([date, dayMessages]) => (
              <div key={date}>
                {/* Date separator - estilo badge/pill centralizado com linhas */}
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 border-t border-border/50" />
                  <span 
                    className="text-xs font-medium px-3 py-1 rounded-full shadow-sm"
                    style={{ backgroundColor: 'hsl(var(--chat-date-bg))', color: 'hsl(var(--chat-text-secondary))' }}
                  >
                    {formatMessageDate(dayMessages[0].sent_at)}
                  </span>
                  <div className="flex-1 border-t border-border/50" />
                </div>

                {/* Messages - Responsivo com espaçamento menor em mobile */}
                <div className="space-y-1.5 md:space-y-2">
                  {dayMessages.map((message, messageIndex) => {
                    // Verifica se precisa exibir card de postagem ANTES desta mensagem
                    const postEntry = getPostForComment(message.id);
                    const isFirstCommentOfPost = postEntry && 
                      postEntry.commentMessageIds[0] === message.id;
                    
                    // Verifica se mensagem é um comentário (para exibir quote)
                    const isComment = isCommentMessage(message.id) && message.direction === 'inbound';
                    
                    // Handler para scroll até o card da postagem
                    const scrollToPostCard = (postId: string) => {
                      const postElement = postCardRefs.current.get(postId);
                      if (postElement) {
                        postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        postElement.classList.add('ring-2', 'ring-amber-500');
                        setTimeout(() => {
                          postElement.classList.remove('ring-2', 'ring-amber-500');
                        }, 2000);
                      }
                    };

                    return (
                      <div key={message.id}>
                        {/* Card da postagem (exibido antes do primeiro comentário) */}
                        {isFirstCommentOfPost && postEntry && (
                          <PostCardBubble
                            postId={postEntry.postId}
                            mediaUrl={postEntry.mediaUrl}
                            mediaType={postEntry.mediaType}
                            mediaPermalink={postEntry.mediaPermalink}
                            timestamp={postEntry.firstCommentAt}
                            commentCount={postEntry.commentCount}
                            innerRef={(el) => {
                              if (el) postCardRefs.current.set(postEntry.postId, el);
                            }}
                          />
                        )}
                        
                        {/* Mensagem */}
                        {(() => {
                          // ----- Modo seleção (encaminhamento múltiplo) -----
                          const isSystem = (message.sender_type as string) === 'system';
                          const canSelect =
                            forwardSelectionMode &&
                            !isSystem &&
                            !message.deleted_at &&
                            isMessageForwardable(message, { isInstagramInbox });
                          const isSelected = selectedForwardIds.has(message.id);
                          return (
                        <div
                          ref={(el) => {
                            if (el) messageRefs.current.set(message.id, el);
                          }}
                          onClick={canSelect ? (e) => {
                            e.stopPropagation();
                            toggleForwardSelection(message);
                          } : undefined}
                          className={isSystem ? 'w-full' : cn(
                            'flex group w-full overflow-hidden items-center gap-2',
                            message.direction === 'outbound' ? 'justify-end' : 'justify-start',
                            forwardSelectionMode && 'transition-colors rounded-md py-0.5',
                            canSelect && 'cursor-pointer hover:bg-muted/40',
                            isSelected && 'bg-primary/10',
                            forwardSelectionMode && !canSelect && 'opacity-60',
                          )}
                        >
                          {/* Checkbox de seleção — apenas em modo de seleção e para mensagens encaminháveis */}
                          {forwardSelectionMode && !isSystem && (
                            <div
                              className={cn(
                                'flex-shrink-0 flex items-center justify-center w-6',
                                message.direction === 'outbound' ? 'order-last' : 'order-first',
                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={!canSelect}
                                onCheckedChange={() => canSelect && toggleForwardSelection(message)}
                                aria-label="Selecionar mensagem para encaminhar"
                              />
                            </div>
                          )}

                          {/* MENSAGEM DE SISTEMA - centralizada e discreta (estilo WhatsApp) */}
                          {/* Verifica se é card de anúncio Meta Ads para renderizar componente especial */}
                          {message.sender_type === 'system' ? (() => {
                            const sysMeta = (message.metadata as Record<string, any>) || {};

                            // Mensagem privada (nota interna da equipe) — v1.0.0
                            // Nunca é enviada ao provider nem ao widget do visitante.
                            if (sysMeta.kind === 'internal_note') {
                              return (
                                <InternalNoteBubble
                                  content={message.content || ''}
                                  authorName={sysMeta.author_name}
                                  authorAvatar={sysMeta.author_avatar}
                                  time={formatMessageTime(message.sent_at)}
                                />
                              );
                            }

                            // Verificar se é mensagem de origem de anúncio (ad_source)
                            const adSource = sysMeta.ad_source;
                            const isAdSourceCard = (message.message_type as string) === 'ad_source' && adSource;
                            

                            
                            if (isAdSourceCard) {
                              // Card visual de origem de anúncio (Click-to-WhatsApp)
                              return (
                                <AdSourceCardBubble
                                  adTitle={adSource.title}
                                  adBody={adSource.body}
                                  thumbnailUrl={adSource.thumbnailUrl}
                                  sourceUrl={adSource.sourceUrl}
                                  sourceApp={adSource.sourceApp}
                                  timestamp={message.sent_at}
                                />
                              );
                            }
                            
                            // Mensagem de sistema padrão
                            return (
                              <div className="w-full flex justify-center py-2">
                                <div 
                                  className="text-muted-foreground 
                                             px-4 py-1.5 rounded-lg text-xs shadow-sm 
                                             border border-border/50 max-w-[70%] text-center"
                                  style={{ backgroundColor: 'hsl(var(--chat-date-bg) / 0.95)' }}
                                >
                                  {message.content}
                                </div>
                              </div>
                            );
                          })() : message.deleted_at ? (
                            /* Mensagem deletada (soft delete) - mostra placeholder */
                            <DeletedMessageBubble
                              direction={message.direction}
                              time={formatMessageTime(message.sent_at)}
                            />
                          ) : (
                            /* Mensagem normal - wrapper que agrupa avatar + botões de ação + balão.
                               Compacto: bolhas usam quase toda a largura disponível. */
                           <div className="flex items-end gap-1 md:gap-1.5 max-w-[92%] sm:max-w-[85%] md:max-w-[70%]">
                              {/* Menu de contexto ANTES do balão para outbound (aparece à esquerda) */}
                              {message.direction === 'outbound' && (
                                <MessageContextMenu
                                  message={message}
                                  currentReaction={getMyReaction(message)}
                                  onReact={(emoji) => onSendReaction?.(message, emoji)}
                                  onReply={() => handleReply(message)}
                                  onDelete={(deleteForEveryone) => onDeleteMessage?.(message, deleteForEveryone)}
                                  onResend={() => onResendMessage?.(message)}
                                  onForward={() => handleForwardFromMenu(message)}
                                  disabled={forwardSelectionMode || (!onSendReaction && !onDeleteMessage)}
                                  currentUserId={currentUserId}
                                  isTenantAdmin={isTenantAdmin}
                                  isInstagramInbox={isInstagramInbox}
                                  isInstagramComment={isCommentMessage(message.id)}
                                />
                              )}

                              {/* Avatar do remetente em GRUPOS (mensagens inbound) - espelha comportamento do WhatsApp nativo */}
                              {conversation.group_id && message.direction === 'inbound' && message.participant_phone && (() => {
                                const { src, name } = getParticipantAvatar(message);
                                return (
                                  <SmartAvatar
                                    src={src || undefined}
                                    name={name}
                                    size="xs"
                                    className="flex-shrink-0 mb-1"
                                    alt={name}
                                  />
                                );
                              })()}
                              
                              {/* Wrapper relativo para posicionar reações - pb-5 garante espaço adequado entre reações e próxima mensagem */}
                              <div className={cn("relative min-w-0", message.reactions?.length ? "pb-5" : "")}>
                                {/* Balão da mensagem - Responsivo com padding menor em mobile, min-w-0 força respeito ao max-width do container pai em flexbox */}
                                <div
                                  className={cn(
                                    'rounded-xl md:rounded-2xl px-2.5 md:px-4 py-1.5 md:py-2 transition-all overflow-hidden min-w-0',
                                    message.direction === 'outbound'
                                      ? 'rounded-br-md'
                                      : 'rounded-bl-md'
                                  )}
                                  style={{
                                    backgroundColor: message.direction === 'outbound' 
                                      ? 'hsl(var(--chat-bubble-outbound))' 
                                      : 'hsl(var(--chat-bubble-inbound))',
                                    color: 'hsl(var(--chat-text-primary))'
                                  }}
                                >
                                  {/* Quote de postagem para comentários Instagram */}
                                  {isComment && postEntry && (
                                    <CommentQuoteBubble
                                      postThumbnail={postEntry.mediaUrl}
                                      postMediaType={postEntry.mediaType}
                                      direction={message.direction}
                                      onQuoteClick={() => scrollToPostCard(postEntry.postId)}
                                    />
                                  )}
                                  
                                  {/* Quote de Story para story_reply e story_mention */}
                                  {/* Também verifica metadata para mensagens antigas com type "text" */}
                                  {(message.message_type === 'story_reply' || 
                                    message.message_type === 'story_mention' || 
                                    message.metadata?.reply_to_story ||
                                    message.metadata?.story_mention) && (() => {
                                    // Extrai dados do story do metadata ou attachments
                                    const storyData = (() => {
                                      // Story reply: dados no metadata.reply_to_story (funciona para type 'text' ou 'story_reply')
                                      if (message.metadata?.reply_to_story) {
                                        const data = message.metadata.reply_to_story as { url?: string; id?: string };
                                        return { ...data, type: 'story_reply' as const };
                                      }
                                      // Story mention: dados no metadata.story_mention ou attachment
                                      if (message.message_type === 'story_mention' || message.metadata?.story_mention) {
                                        if (message.metadata?.story_mention) {
                                          const data = message.metadata.story_mention as { url?: string; id?: string };
                                          return { ...data, type: 'story_mention' as const };
                                        }
                                        // Fallback: tentar attachment com URL de mídia
                                        const storyAttachment = message.attachments?.find(a => a.type === 'story_mention' || a.type === 'image');
                                        if (storyAttachment?.url) {
                                          return { 
                                            url: storyAttachment.url, 
                                            id: storyAttachment.metadata?.story_id as string,
                                            type: 'story_mention' as const
                                          };
                                        }
                                      }
                                      return null;
                                    })();
                                    
                                    // Só renderiza se tiver URL
                                    if (!storyData?.url) return null;
                                    
                                    return (
                                      <StoryQuoteBubble
                                        storyUrl={storyData.url}
                                        direction={message.direction}
                                        messageType={storyData.type || 'story_reply'}
                                      />
                                    );
                                  })()}
                                  
                                  {/* Mensagem citada (se for reply) */}
                                  {message.reply_to && (
                                    <QuotedMessageBubble
                                      quotedMessage={message.reply_to}
                                      direction={message.direction}
                                      onQuoteClick={() => scrollToMessage(message.reply_to!.id)}
                                    />
                                  )}
                                  
                                  {/* Nome do remetente (para grupos, apenas em mensagens inbound) */}
                                  {conversation.group_id && message.direction === 'inbound' && message.participant_phone && (
                                    <p className="text-xs font-medium text-primary mb-1">
                                      {getParticipantName(message)}
                                    </p>
                                  )}
                                  
                                  {/* Badge de origem Instagram (story_mention, story_reply, reel_share, share) */}
                                  {isInstagramSpecialType(message.message_type) && (
                                    <InstagramSourceBadge 
                                      messageType={message.message_type} 
                                      direction={message.direction}
                                    />
                                  )}

                              {/* Badge "Encaminhada" — exibido acima do conteúdo quando a
                                  mensagem foi gerada via forward_message_create_job */}
                              {(message.metadata as { forwarded?: boolean } | null)?.forwarded && (
                                <div
                                  className="flex items-center gap-1 mb-1 text-[11px] italic"
                                  style={{ color: 'hsl(var(--chat-text-secondary))' }}
                                >
                                  <Forward className="h-3 w-3" />
                                  <span>Encaminhada</span>
                                </div>
                              )}
                              {renderMessageContent(message)}
                              <div
                                className={cn(
                                  'flex items-center gap-1 mt-1',
                                  message.direction === 'outbound' ? 'justify-end' : 'justify-start'
                                )}
                              >
                                <span
                                  className="text-[10px]"
                                  style={{ color: 'hsl(var(--chat-text-secondary))' }}
                                >
                                  {formatMessageTime(message.sent_at)}
                                </span>
                                <MessageStatusIndicator
                                  status={message.status}
                                  direction={message.direction}
                                  className="text-[hsl(var(--chat-text-secondary))]"
                                  onErrorClick={() => handleErrorClick(message)}
                                  hasErrorDetails={message.status === 'failed'}
                                />
                              </div>
                            </div>
                            
                            {/* Reações abaixo do balão */}
                            <MessageReactions
                              reactions={message.reactions}
                              direction={message.direction}
                            />
                          </div>

                          {/* Avatar do remetente para mensagens outbound */}
                          {message.direction === 'outbound' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex-shrink-0 mb-1">
                                  {/* IDENTIFICAÇÃO DE ORIGEM: Verificar se veio do celular WhatsApp conectado */}
                                  {(() => {
                                    // Tipagem para acessar metadata.z_api_payload ou waha_payload
                                    const metadata = message.metadata as { 
                                      z_api_payload?: { sent_from_phone?: boolean };
                                      waha_payload?: { sent_from_phone?: boolean };
                                      waba_payload?: { sent_from_phone?: boolean };
                                    } | null;
                                    const sentFromPhone = metadata?.z_api_payload?.sent_from_phone 
                                                       || metadata?.waha_payload?.sent_from_phone
                                                       || metadata?.waba_payload?.sent_from_phone;
                                    
                                    // Se veio do celular, mostrar ícone de telefone
                                    if (sentFromPhone) {
                                      return (
                                        <div className="h-5 w-5 md:h-6 md:w-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                          <Smartphone className="h-3 w-3 md:h-3.5 md:w-3.5 text-green-600 dark:text-green-400" />
                                        </div>
                                      );
                                    }
                                    
                                    // Renderiza avatar baseado no sender_type (lógica existente)
                                    if (message.sender_type === 'agent' && message.sender) {
                                      return (
                                        <SmartAvatar
                                          src={message.sender.avatar_url}
                                          name={message.sender.full_name}
                                          size="xs"
                                          className="border border-primary/20"
                                          fallbackClassName="bg-primary/10 text-primary"
                                          alt={message.sender.full_name || 'Agente'}
                                        />
                                      );
                                    }
                                    if (message.sender_type === 'bot') {
                                      return (
                                        <div className="h-5 w-5 md:h-6 md:w-6 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                          <Bot className="h-3 w-3 md:h-3.5 md:w-3.5 text-violet-600 dark:text-violet-400" />
                                        </div>
                                      );
                                    }
                                    if (message.sender_type === 'campaign') {
                                      return (
                                        <div className="h-5 w-5 md:h-6 md:w-6 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                          <Megaphone className="h-3 w-3 md:h-3.5 md:w-3.5 text-orange-600 dark:text-orange-400" />
                                        </div>
                                      );
                                    }
                                    if (message.sender_type === 'routing') {
                                      return (
                                        <div className="h-5 w-5 md:h-6 md:w-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                          <Zap className="h-3 w-3 md:h-3.5 md:w-3.5 text-amber-600 dark:text-amber-400" />
                                        </div>
                                      );
                                    }
                                    {/* Ícone dedicado para mensagens de lembrete automático */}
                                    if (message.sender_type === 'reminder') {
                                      return (
                                         <div className="h-5 w-5 md:h-6 md:w-6 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                           <AlarmClock className="h-3 w-3 md:h-3.5 md:w-3.5 text-violet-600 dark:text-violet-400" />
                                        </div>
                                      );
                                    }
                                    if (message.sender_type === 'agent') {
                                      // Agente sem dados de sender (fallback)
                                      return (
                                        <Avatar className="h-5 w-5 md:h-6 md:w-6 border border-primary/20">
                                          <AvatarFallback className="text-[8px] md:text-[9px] bg-primary/10 text-primary">
                                            A
                                          </AvatarFallback>
                                        </Avatar>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="text-xs">
                                {/* Tooltip com origem da mensagem */}
                                {(() => {
                                  const metadata = message.metadata as { 
                                    z_api_payload?: { sent_from_phone?: boolean };
                                    waha_payload?: { sent_from_phone?: boolean };
                                    waba_payload?: { sent_from_phone?: boolean };
                                  } | null;
                                  const sentFromPhone = metadata?.z_api_payload?.sent_from_phone 
                                                     || metadata?.waha_payload?.sent_from_phone
                                                     || metadata?.waba_payload?.sent_from_phone;
                                  
                                  if (sentFromPhone) return 'Enviado pelo Celular';
                                  if (message.sender_type === 'agent' && message.sender?.full_name) 
                                    return message.sender.full_name;
                                  if (message.sender_type === 'agent') return 'Agente';
                                  if (message.sender_type === 'bot') return 'Fluxo Automático';
                                   if (message.sender_type === 'campaign') return 'Campanha';
                                   if (message.sender_type === 'reminder') return 'Lembrete Automático';
                                   if (message.sender_type === 'routing') return 'Ação Rápida';
                                   return 'Remetente';
                                })()}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {/* Menu de contexto DEPOIS do balão para inbound (aparece à direita) */}
                          {message.direction === 'inbound' && (
                            <MessageContextMenu
                              message={message}
                              currentReaction={getMyReaction(message)}
                              onReact={(emoji) => onSendReaction?.(message, emoji)}
                              onReply={() => handleReply(message)}
                              onDelete={(deleteForEveryone) => onDeleteMessage?.(message, deleteForEveryone)}
                              onResend={() => onResendMessage?.(message)}
                              onForward={() => handleForwardFromMenu(message)}
                              disabled={forwardSelectionMode || (!onSendReaction && !onDeleteMessage)}
                              currentUserId={currentUserId}
                              isTenantAdmin={isTenantAdmin}
                              isInstagramInbox={isInstagramInbox}
                              isInstagramComment={isCommentMessage(message.id)}
                            />
                          )}
                        </div>
                      )}
                        </div>
                        );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
              
              {/* ========== INDICADOR DE DIGITAÇÃO/GRAVAÇÃO (BOLHA) ========== */}
              {/* Exibido apenas em conversas 1:1 (não grupos) quando contato está digitando/gravando */}
              {/* Componente com animação de entrada/saída suave */}
              {!conversation.group_id && (contactPresence?.isTyping || contactPresence?.isRecording) && (
                <TypingBubble
                  isTyping={contactPresence.isTyping}
                  isRecording={contactPresence.isRecording}
                  contactName={conversation.contact?.name || undefined}
                />
              )}
              
              {/* ========== MODO ANCORADO: Botão "Carregar mensagens mais recentes" ========== */}
              {/* Aparece no fim da janela ancorada quando ainda existem mensagens posteriores no DB. */}
              {isAnchoredMode && hasMoreNewer && (
                <div className="flex justify-center py-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onLoadMoreNewer}
                    disabled={loadingMoreNewer}
                    className="gap-2"
                  >
                    {loadingMoreNewer ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    {loadingMoreNewer ? 'Carregando...' : 'Carregar mensagens mais recentes'}
                  </Button>
                </div>
              )}

              {/* Anchor element for auto-scroll */}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* ========== MODO ANCORADO: Pílula flutuante "Ir para o fim" ========== */}
        {/* Mostra contador de novas mensagens recebidas via realtime enquanto o modo */}
        {/* ancorado está ativo. Ao clicar, sai do modo ancorado e recarrega o fim.   */}
        {isAnchoredMode && (newMessagesWhileAnchored ?? 0) > 0 && (
          <button
            type="button"
            onClick={onExitAnchorMode}
            className="absolute left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg hover:opacity-90 transition-opacity flex items-center gap-1.5 pointer-events-auto"
            style={{ bottom: inputOverlayHeight + 16 }}
          >
            <ChevronDown className="h-3.5 w-3.5" />
            {newMessagesWhileAnchored} nova{(newMessagesWhileAnchored ?? 0) > 1 ? 's' : ''} mensage{(newMessagesWhileAnchored ?? 0) > 1 ? 'ns' : 'm'} — Ir para o fim
          </button>
        )}
        {/* ========== MODO HISTÓRICO: Pílula flutuante "Novas mensagens" ========== */}
        {/* Ativado ao clicar em "Carregar mensagens anteriores". Conta mensagens   */}
        {/* novas recebidas via realtime sem mover a viewport. Clique rola ao fim.  */}
        {!isAnchoredMode && isHistoryBrowsingMode && historyNewMessagesCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setIsHistoryBrowsingMode(false);
              setHistoryNewMessagesCount(0);
              scrollToBottom(false);
            }}
            className="absolute left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg hover:opacity-90 transition-opacity flex items-center gap-1.5 pointer-events-auto"
            style={{ bottom: inputOverlayHeight + 16 }}
          >
            <ChevronDown className="h-3.5 w-3.5" />
            {historyNewMessagesCount} nova{historyNewMessagesCount > 1 ? 's' : ''} mensage{historyNewMessagesCount > 1 ? 'ns' : 'm'} — Ir para o fim
          </button>
        )}
        {/* ========== MODO HISTÓRICO: Botão flutuante "Ir para o fim" ========== */}
        {/* Atalho estilo WhatsApp Web — visível apenas em modo histórico.        */}
        {/* Clique rola instantaneamente até a última mensagem e sai do modo.     */}
        {!isAnchoredMode && isHistoryBrowsingMode && (
          <button
            type="button"
            aria-label="Ir para a última mensagem"
            onClick={() => {
              setIsHistoryBrowsingMode(false);
              setHistoryNewMessagesCount(0);
              scrollToBottom(false);
            }}
            className="absolute right-4 z-20 h-10 w-10 rounded-full bg-card border border-border shadow-lg hover:bg-accent transition-colors flex items-center justify-center pointer-events-auto"
            style={{ bottom: inputOverlayHeight + 16 }}
          >
            <ChevronDown className="h-5 w-5 text-foreground" />
          </button>
        )}



        {/* Barra de ações do modo "Encaminhar múltiplas" — substitui o composer enquanto ativa. */}
        {forwardSelectionMode && (
          <div className="absolute bottom-0 left-0 right-0 z-20 p-2 md:p-4">
            <div className="mx-auto max-w-3xl rounded-lg border bg-card shadow-lg flex items-center justify-between gap-3 px-4 py-3">
              <div className="text-sm">
                <span className="font-medium">{selectedForwardIds.size}</span>
                <span className="text-muted-foreground"> / {FORWARD_MAX_MESSAGES} mensagem(ns) selecionada(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={exitForwardSelection}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleOpenForwardSheet}
                  disabled={selectedForwardIds.size === 0}
                  className="gap-2"
                >
                  <Forward className="h-4 w-4" />
                  Encaminhar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Input - Overlay absoluto (remove 100% o "rodapé" em fluxo normal) */}
        <div
          ref={inputOverlayRef}
          className={cn(
            "absolute bottom-0 left-0 right-0 z-10 p-2 md:p-4 pointer-events-none",
            forwardSelectionMode && "hidden",
          )}
        >
          {/*
            pointer-events:
            - O container do overlay é pointer-events-none para não bloquear scroll na área vazia.
            - Os elementos interativos ficam em pointer-events-auto.
          */}
          {/* FAB lateral esquerdo ancorado logo acima do composer (bottom-full) */}
          {conversation?.id && (
            <div className="absolute bottom-full left-1 sm:left-3 pointer-events-auto">
              {/* FAB sobe mais 5% (translate-y[5%] → removido / 0%) */}

              <ChatQuickActionsFab
                conversationId={conversation.id}
                contactId={conversation?.contact_id || conversation?.contact?.id}
                onOpenChange={setFabOpen}
                conversationContext={{
                  inboxId: conversation?.inbox_id,
                  inboxName: conversation?.inbox?.name,
                  channelType: conversation?.inbox?.channel_type,
                  // v2.2.0 — usado pelo ScheduleMessageDialog para janela 24h
                  lastInboundAt: (conversation as any)?.last_inbound_at ?? null,
                }}
              />
            </div>
          )}
          <div className="pointer-events-auto">

            {/* Alerta de janela 24h expirada (WhatsApp Oficial) */}
            {/* Escondido no modo "Mensagem privada": nota interna não depende da janela 24h */}
            {isSessionWindowExpired && composerMode !== 'internal' && onOpenTemplateDialog && conversation?.inbox?.channel_type !== 'instagram' && (
              <SessionWindowAlert 
                onSendTemplate={onOpenTemplateDialog}
                disabled={isAgentWithoutAssignment}
              />
            )}

            {/* Alerta de janela 24h expirada (Instagram Direct - sem templates) */}
            {/* Esconde alerta quando agente está respondendo a um comentário (sem restrição 24h) */}
            {/* ou quando está em "Mensagem privada" (nota interna, sem envio ao provider) */}
            {isSessionWindowExpired && composerMode !== 'internal' && conversation?.inbox?.channel_type === 'instagram' && !isReplyingToComment && (
              <InstagramSessionExpiredAlert />
            )}

            {/* Preview de reply (se estiver respondendo e janela ativa, respondendo comentário) */}
            {/* ou em "Mensagem privada" — a citação é apenas contexto visual da nota interna */}
            {/* Inclui badge indicando tipo de resposta para Instagram */}
            {replyingTo && (!isSessionWindowExpired || isReplyingToComment || composerMode === 'internal') && (
              <div className="relative">
                <ReplyPreview
                  message={replyingTo}
                  onCancel={() => setReplyingTo(null)}
                />
                {/* Badge indicador do tipo de resposta (Instagram) */}
                {isInstagramInbox && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'absolute -top-2 right-8 text-[10px] font-normal shadow-sm',
                      isCommentMessage(replyingTo.id)
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800'
                    )}
                  >
                    {isCommentMessage(replyingTo.id) ? '📢 Comentário público' : '💬 Direct'}
                  </Badge>
                )}
              </div>
            )}




            {/* ===== Abas do composer: Responder x Mensagem privada ===== */}
            {/* A nota interna reaproveita a estrutura de mensagens de sistema:
                fica visível na conversa para a equipe, mas nunca é enviada ao
                contato/provider nem ao widget do visitante. */}
            {onSendInternalNote && conversation?.id && (
              /* Compacto: rótulos curtos e paddings menores para caber em uma linha */
              <div className="flex items-center gap-1 px-1 pb-1 flex-nowrap overflow-hidden">
                <button
                  type="button"
                  onClick={() => setComposerMode('reply')}
                  className={cn(
                    'text-[11px] px-2 sm:px-3 py-1 rounded-t-md border-b-2 transition-colors whitespace-nowrap',
                    composerMode === 'reply'
                      ? 'border-primary text-foreground font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  Responder
                </button>
                <button
                  type="button"
                  onClick={() => setComposerMode('internal')}
                  className={cn(
                    'text-[11px] px-2 sm:px-3 py-1 rounded-t-md border-b-2 transition-colors inline-flex items-center gap-1 whitespace-nowrap',
                    composerMode === 'internal'
                      ? 'border-amber-500 text-amber-700 dark:text-amber-300 font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Lock className="h-3 w-3 shrink-0" />
                  {isCompact ? 'Privada' : 'Mensagem privada'}
                </button>
              </div>
            )}

            {/* Barra de input flutuante com bordas arredondadas estilo WhatsApp */}
            <div
              className={cn(
                'flex items-end gap-0.5 sm:gap-1 md:gap-2 px-1.5 sm:px-2 md:px-3 py-1.5 md:py-2',
                composerMode === 'internal' &&
                  'border border-amber-300 dark:border-amber-600/50'
              )}
              style={{
                backgroundColor:
                  composerMode === 'internal'
                    ? 'hsl(45 93% 90%)'
                    : 'hsl(var(--chat-input-bg))',
                borderRadius: '24px',
                boxShadow: '0 1px 3px hsl(var(--chat-text-primary) / 0.12)',
              }}
            >

              {/* Modo Observador: botão de ícone (olho) alinhado aos demais ícones.
                  A chave liga/desliga foi removida — o próprio olho ativa/desativa.
                  Mesmo comportamento em desktop e mobile. */}
              {canUseObserverMode && onToggleObserverMode && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-pressed={isObserverModeActive}
                      onClick={onToggleObserverMode}
                      className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 text-foreground hover:text-primary"
                    >
                      {/* Modo ativo: olho com risco (EyeOff) — visualização clara de que está observando */}
                      {isObserverModeActive ? (
                        <EyeOff className="h-5 w-5 text-foreground" />
                      ) : (
                        <Eye className="h-5 w-5 text-foreground" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">
                      {isObserverModeActive
                        ? 'Modo Observador ativo - Clique para desativar'
                        : 'Ativar Modo Observador (visualizar sem marcar como lida)'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}


              {/* Botão de anexos — oculto no modo Mensagem Privada (não se aplica a notas internas) */}
              {composerMode !== 'internal' && (
                <AttachmentButton
                  onImagesSelect={handleImagesSelect}
                  onVideoSelect={handleVideoSelect}
                  onDocumentSelect={handleDocumentSelect}
                  onLocationSelect={() => setShowLocationDialog(true)}
                  onContactSelect={() => setShowContactDialog(true)}
                  onPixSelect={onSendPix ? () => setShowPixDialog(true) : undefined}
                  disabled={isInputDisabled}
                  providerSlug={conversation?.inbox?.provider_slug}
                />
              )}

              {/* Botão de emojis — oculto em telas compactas (o teclado do celular já
                  oferece emojis) para liberar espaço ao campo de texto */}
              {!isCompact && (
              <EmojiPicker
                onSelect={(emoji) => {
                  // Insere emoji na posição atual do cursor
                  const input = inputRef.current;
                  if (input) {
                    const start = input.selectionStart || 0;
                    const end = input.selectionEnd || 0;
                    const newValue = inputValue.slice(0, start) + emoji + inputValue.slice(end);
                    setInputValue(newValue);
                    // Reposiciona cursor após o emoji
                    setTimeout(() => {
                      input.focus();
                      input.setSelectionRange(start + emoji.length, start + emoji.length);
                    }, 0);
                  } else {
                    setInputValue((prev) => prev + emoji);
                  }
                }}
                disabled={isInputDisabled}
              />
              )}







              {/* Input com suporte a atalho "/" para ações rápidas */}
              <QuickActionsPopover
                open={showQuickActions && !isInputDisabled}
                onOpenChange={(open) => {
                  // CORREÇÃO: Só permite abrir o popover se inputValue começar com "/"
                  // Isso previne abertura ao clicar/focar no input (comportamento do PopoverTrigger)
                  if (open && !inputValue.startsWith('/')) {
                    return; // Ignora tentativa de abertura sem "/"
                  }
                  setShowQuickActions(open);
                  if (!open) {
                    setQuickActionSearch('');
                  }
                }}
                searchQuery={quickActionSearch}
                tenantId={tenantId || ''}
                inboxId={conversation?.inbox_id || ''}
                conversationId={conversation?.id || ''}
                contactId={conversation?.contact_id || ''}
                contactPhone={conversation?.contact?.phone || ''}
                contactName={conversation?.contact?.name}
                replyMode={
                  isInstagramInbox && replyingTo && isCommentMessage(replyingTo.id)
                    ? 'comment_public' : undefined
                }
                commentId={
                  isInstagramInbox && replyingTo && isCommentMessage(replyingTo.id)
                    ? (replyingTo.metadata as { comment_id?: string })?.comment_id : undefined
                }
                replyToMessageId={replyingTo?.id}
                onExecuted={() => {
                  setInputValue('');
                  setShowQuickActions(false);
                  setQuickActionSearch('');
                  setReplyingTo(null); // Limpa reply após executar ação rápida
                  inputRef.current?.focus();
                }}
              >
                {/* Textarea expansível estilo WhatsApp Web */}
                <div className="flex-1 min-w-0 relative">
                  <MentionsPopover
                    open={showMentions && !!conversation?.group_id && !isInputDisabled}
                    searchQuery={mentionQuery}
                    participants={groupParticipants}
                    onSelect={(p) => {
                      // Substitui "@<query>" por "@<Nome> " no texto e marca como menção ativa
                      if (mentionAnchor === null) return;
                      const before = inputValue.slice(0, mentionAnchor);
                      const afterStart = mentionAnchor + 1 + mentionQuery.length;
                      const after = inputValue.slice(afterStart);
                      const insertion = `@${p.name} `;
                      const newValue = before + insertion + after;
                      setInputValue(newValue);
                      setSelectedMentions((prev) => {
                        const next = new Map(prev);
                        next.set(p.phone, p.name);
                        return next;
                      });
                      setShowMentions(false);
                      setMentionQuery('');
                      setMentionAnchor(null);
                      // Reposiciona o cursor após a inserção
                      requestAnimationFrame(() => {
                        const ta = inputRef.current;
                        if (!ta) return;
                        const pos = before.length + insertion.length;
                        ta.focus();
                        ta.setSelectionRange(pos, pos);
                      });
                    }}
                  >
                    <textarea
                      ref={inputRef}
                      placeholder={
                        // Modo "Mensagem privada" tem placeholder próprio
                        composerMode === 'internal'
                          ? (isMobile
                              ? 'Nota interna (@ menciona)'
                              : 'Nota interna da equipe — digite @ para mencionar um usuário')
                        // Mobile usa textos curtos para caber em uma única linha
                        : hasPendingTransferWaiting
                          ? (isMobile ? 'Inicie o atendimento' : 'Clique em "Iniciar" para começar o atendimento')
                          : isObserverModeActive
                            ? (isMobile ? 'Observador ativo' : 'Modo Observador ativo - desative para enviar')
                            : isInputDisabled && groupPermissionMessage
                              ? 'Sem permissão'
                              : isMobile
                                ? 'Mensagem'
                                : conversation?.group_id
                                  ? 'Digite / para ações rápidas ou @ para mencionar'
                                  : 'Digite / para ações rápidas'

                      }
                      value={inputValue}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setInputValue(newValue);

                        // Detecta "/" no início para abrir ações rápidas
                        if (newValue.startsWith('/')) {
                          setShowQuickActions(true);
                          setQuickActionSearch(newValue.slice(1));
                        } else {
                          setShowQuickActions(false);
                          setQuickActionSearch('');
                        }

                        // ===== Detecção de @menção de USUÁRIOS (mensagem privada) =====
                        if (composerMode === 'internal') {
                          const caret = e.target.selectionStart ?? newValue.length;
                          const upToCaret = newValue.slice(0, caret);
                          const atIndex = upToCaret.lastIndexOf('@');
                          if (atIndex >= 0) {
                            const between = upToCaret.slice(atIndex + 1);
                            const valid = /^[\p{L}\p{N}._\- ]{0,30}$/u.test(between);
                            const prevChar = atIndex > 0 ? upToCaret[atIndex - 1] : '';
                            const isWordBoundary = !prevChar || /\s/.test(prevChar);
                            if (valid && isWordBoundary) {
                              setUserMentionAnchor(atIndex);
                              setUserMentionQuery(between);
                              setShowUserMentions(true);
                            } else {
                              setShowUserMentions(false);
                              setUserMentionQuery('');
                              setUserMentionAnchor(null);
                            }
                          } else {
                            setShowUserMentions(false);
                            setUserMentionQuery('');
                            setUserMentionAnchor(null);
                          }
                        }

                        // ===== Detecção de @menção (apenas em grupos) =====
                        // Procura o último "@" antes do cursor sem espaço/quebra entre ele e o cursor.
                        if (conversation?.group_id && composerMode === 'reply') {
                          const caret = e.target.selectionStart ?? newValue.length;
                          const upToCaret = newValue.slice(0, caret);
                          const atIndex = upToCaret.lastIndexOf('@');
                          if (atIndex >= 0) {
                            const between = upToCaret.slice(atIndex + 1);
                            // Aceita letras, números e acentos (sem espaços/quebras)
                            const valid = /^[\p{L}\p{N}._-]*$/u.test(between);
                            // Garante que o "@" não esteja colado em letra/número (ex: email)
                            const prevChar = atIndex > 0 ? upToCaret[atIndex - 1] : '';
                            const isWordBoundary = !prevChar || /\s/.test(prevChar);
                            if (valid && isWordBoundary) {
                              setMentionAnchor(atIndex);
                              setMentionQuery(between);
                              setShowMentions(true);
                            } else {
                              setShowMentions(false);
                              setMentionQuery('');
                              setMentionAnchor(null);
                            }
                          } else {
                            setShowMentions(false);
                            setMentionQuery('');
                            setMentionAnchor(null);
                          }
                        }


                        if (newValue && onTyping) {
                          onTyping();
                        }
                      }}
                      onPaste={(e) => {
                        // Cola de imagem da área de transferência (Ctrl+V / Cmd+V).
                        // Detecta arquivos do tipo imagem e abre o diálogo de envio,
                        // reutilizando o mesmo fluxo de validação dos anexos.
                        if (isInputDisabled) return;
                        const items = e.clipboardData?.items;
                        if (!items || items.length === 0) return;

                        const pastedImages: File[] = [];
                        for (let i = 0; i < items.length; i++) {
                          const item = items[i];
                          if (item.kind === 'file' && item.type.startsWith('image/')) {
                            const file = item.getAsFile();
                            if (file) {
                              // Renomeia arquivo genérico do clipboard com timestamp
                              const ext = (file.type.split('/')[1] || 'png').split('+')[0];
                              const named = file.name && file.name !== 'image.png'
                                ? file
                                : new File(
                                    [file],
                                    `clipboard-${Date.now()}.${ext}`,
                                    { type: file.type }
                                  );
                              pastedImages.push(named);
                            }
                          }
                        }

                        if (pastedImages.length > 0) {
                          // Impede que a "imagem" seja inserida como texto no textarea
                          e.preventDefault();
                          processDroppedImages(pastedImages);
                        }
                      }}
                      onKeyDown={(e) => {
                        // No modo privado, o popover reutilizado escuta Enter/setas/Escape
                        // no document para selecionar e navegar entre usuários. Enquanto
                        // ele estiver aberto, não encaminhamos essas teclas ao handleKeyDown,
                        // pois o Enter também enviaria a nota no mesmo evento da seleção.
                        if (
                          composerMode === 'internal' &&
                          showUserMentions &&
                          ['Enter', 'ArrowDown', 'ArrowUp', 'Escape'].includes(e.key)
                        ) {
                          return;
                        }

                        // Esc fecha o seletor de menções antes de qualquer outra ação
                        if (showMentions && e.key === 'Escape') {
                          e.preventDefault();
                          setShowMentions(false);
                          return;
                        }
                        handleKeyDown(e);
                      }}
                      rows={1}
                      className={cn(
                        "w-full resize-none border-transparent bg-transparent",
                        "focus-visible:outline-none focus-visible:ring-0",
                        "placeholder:text-muted-foreground placeholder:text-[0.65rem]",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "text-sm py-2.5 px-1"
                      )}
                      style={{
                        minHeight: '40px',
                        maxHeight: '160px',
                        overflowY: 'auto',
                      }}
                      disabled={isInputDisabled}
                    />
                  </MentionsPopover>

                  {/* Popover de @menções de USUÁRIOS (somente no modo "Mensagem privada").
                      Reaproveita o componente já usado nos comentários do CRM. */}
                  {composerMode === 'internal' && (
                    <UserMentionPopover
                      isOpen={showUserMentions && filteredMentionUsers.length > 0}
                      onClose={() => setShowUserMentions(false)}
                      users={filteredMentionUsers}
                      searchTerm={userMentionQuery}
                      anchorRef={inputRef}
                      onSelect={(u) => {
                        if (userMentionAnchor === null) return;
                        const before = inputValue.slice(0, userMentionAnchor);
                        const afterStart = userMentionAnchor + 1 + userMentionQuery.length;
                        const after = inputValue.slice(afterStart);
                        // No textarea mostramos apenas "@Nome" (sem UUID).
                        // O formato oficial `@[Nome](uuid)` é remontado no envio.
                        const insertion = `@${u.full_name} `;
                        const newValue = before + insertion + after;
                        setInputValue(newValue);
                        setMentionUserMap((prev) => ({ ...prev, [u.full_name]: u.id }));
                        setShowUserMentions(false);
                        setUserMentionQuery('');
                        setUserMentionAnchor(null);
                        requestAnimationFrame(() => {
                          const ta = inputRef.current;
                          if (!ta) return;
                          const pos = before.length + insertion.length;
                          ta.focus();
                          ta.setSelectionRange(pos, pos);
                        });
                      }}
                    />
                  )}
                </div>
              </QuickActionsPopover>

              {/* Mostra botão de enviar texto se tiver texto, senão mostra microfone.
                  No modo Mensagem Privada o microfone é oculto (notas internas não suportam áudio),
                  então o botão de enviar (Lock) aparece sempre. */}
              {inputValue.trim() || composerMode === 'internal' ? (
                <Button
                  onClick={handleSend}
                  disabled={isInputDisabled || sendingInternalNote}
                  size="icon"
                  className={cn(
                    'h-8 w-8 sm:h-9 sm:w-9 shrink-0',
                    composerMode === 'internal' &&
                      'bg-amber-500 hover:bg-amber-600 text-amber-950'
                  )}
                  title={composerMode === 'internal' ? 'Adicionar nota interna' : 'Enviar'}
                >
                  {composerMode === 'internal' ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}

                </Button>
              ) : (
                <AudioRecorderButton
                  onSend={onSendAudio}
                  disabled={isInputDisabled}
                  providerSlug={conversation?.inbox?.provider_slug}
                />
              )}

              {/* Botão de atalho para abrir Sheet lateral de Ações Rápidas — oculto no modo Mensagem Privada */}
              {composerMode !== 'internal' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 text-foreground hover:text-amber-500"
                      disabled={isInputDisabled}
                      onClick={() => setShowQuickActionsSheet(true)}
                      aria-label="Ações rápidas"
                    >
                      <Zap className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">Ações rápidas</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Sheet lateral de Ações Rápidas (atalho do botão de raio) */}
            <QuickActionsSheet
              open={showQuickActionsSheet}
              onOpenChange={setShowQuickActionsSheet}
              tenantId={tenantId || ''}
              inboxId={conversation?.inbox_id || ''}
              conversationId={conversation?.id || ''}
              contactId={conversation?.contact_id || ''}
              contactPhone={conversation?.contact?.phone || ''}
              contactName={conversation?.contact?.name}
              replyMode={
                isInstagramInbox && replyingTo && isCommentMessage(replyingTo.id)
                  ? 'comment_public' : undefined
              }
              commentId={
                isInstagramInbox && replyingTo && isCommentMessage(replyingTo.id)
                  ? (replyingTo.metadata as { comment_id?: string })?.comment_id : undefined
              }
              replyToMessageId={replyingTo?.id}
              onExecuted={() => {
                // Limpa reply pendente após executar via sheet
                setReplyingTo(null);
                inputRef.current?.focus();
              }}
            />

            {/* Mensagens explicativas sobre restrições */}
            {isClosed && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Esta conversa está encerrada. Reabra para enviar mensagens.
              </p>
            )}
            {!isClosed && groupPermissionMessage && (
              <div className="flex items-center justify-center gap-2 mt-2 text-xs text-amber-600">
                <ShieldAlert className="h-3 w-3" />
                <p>{groupPermissionMessage}</p>
              </div>
            )}
            {!isClosed && isAgentWithoutAssignment && (
              <div className="flex items-center justify-center gap-2 mt-2 text-xs text-amber-600">
                <ShieldAlert className="h-3 w-3" />
                <p>Clique em "Assumir" para iniciar o atendimento desta conversa.</p>
              </div>
            )}
            {/* Mensagem explicativa para Transferência Pendente */}
            {!isClosed && hasPendingTransferWaiting && (
              <div className="flex items-center justify-center gap-2 mt-2 text-xs text-blue-600">
                <PlayCircle className="h-3 w-3" />
                <p>Clique em "Iniciar" para começar o atendimento desta conversa.</p>
              </div>
            )}
            {/* [NOVO] Banner informativo para modo somente-leitura (inbox sem permissão de envio) */}
            {isReadOnlyMode && (
              <div className="flex items-center justify-center gap-2 mt-2 text-xs text-amber-700 dark:text-amber-400">
                <Eye className="h-3 w-3 shrink-0" />
                <p>Você está no modo visualização. Sem permissão para enviar mensagens nesta inbox.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialog de envio de múltiplas imagens */}
      <SendMultipleImagesDialog
        open={showImageDialog}
        onClose={handleCloseImageDialog}
        imageFiles={selectedImageFiles}
        onSend={onSendImages}
      />

      {/* Lightbox para visualizar imagens (suporta galeria) */}
      <ImageLightbox
        open={!!lightboxData}
        onClose={() => setLightboxData(null)}
        images={lightboxData?.images}
        initialIndex={lightboxData?.initialIndex || 0}
        caption={lightboxData?.caption}
      />

      {/* Dialog de envio de documento */}
      <SendDocumentDialog
        open={showDocumentDialog}
        onClose={handleCloseDocumentDialog}
        documentFile={selectedDocumentFile}
        onSend={onSendDocument}
      />

      {/* Dialog de envio de vídeo */}
      <SendVideoDialog
        open={showVideoDialog}
        onClose={handleCloseVideoDialog}
        videoFile={selectedVideoFile}
        onSend={onSendVideo}
      />

      {/* Dialog de envio de localização */}
      <SendLocationDialog
        open={showLocationDialog}
        onClose={() => setShowLocationDialog(false)}
        onSend={onSendLocation}
      />

      {/* Dialog de envio de contato */}
      <SendContactDialog
        open={showContactDialog}
        onClose={() => setShowContactDialog(false)}
        onSend={onSendContact}
        tenantId={tenantId}
      />

      {/* Dialog de envio de PIX */}
      {onSendPix && (
        <SendPixDialog
          open={showPixDialog}
          onClose={() => setShowPixDialog(false)}
          onSend={onSendPix}
        />
      )}

      {/* Dialog de transferência de conversa */}
      <TransferConversationDialog
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        conversationId={conversation.id}
        currentAgentId={conversation.assigned_to}
       inboxId={conversation.inbox_id}
       channelType={conversation.inbox?.channel_type}
      />

      {/* Dialog de transferência para departamento */}
      <TransferToDepartmentDialog
        open={showTransferDeptDialog}
        onOpenChange={setShowTransferDeptDialog}
        conversationId={conversation.id}
        currentDepartmentId={conversation.department_id}
        onSuccess={onRefreshConversation}
      />
      
      {/* Dialog de gerenciamento de etiquetas - apenas se provider suporta */}
      {labelsSupported && (
        <AssignLabelsDialog
          open={showLabelsDialog}
          onOpenChange={setShowLabelsDialog}
          availableLabels={availableLabels}
          currentLabelIds={conversationLabels.map(cl => cl.label_id)}
          isSaving={assigningLabels}
          conversationName={conversation.contact?.name || conversation.group?.name}
          onSave={async (labelIds) => {
            // Monta o chatId no formato esperado pelo provider
            const chatId = conversation.group_id 
              ? conversation.group?.external_id || ''
              : `${conversation.contact?.phone?.replace(/\D/g, '')}@c.us`;
            
            await assignLabels({
              conversationId: conversation.id,
              labelIds,
              chatId,
            });
          }}
        />
      )}

      {/* Dialog de tags do contato - atalho do header */}
      {contactTagsDialogContact && (
        <AssignTagsDialog
          open={showContactTagsDialog}
          onOpenChange={setShowContactTagsDialog}
          contact={contactTagsDialogContact}
          availableTags={availableContactTags}
          conversationId={conversation.id}
          tenantId={effectiveTenantId || ''}
          onSuccess={() => {
            // Mesma regra do ContactSidebar: fonte única de verdade entre
            // o atalho do header e o card de Tags do sidebar.
            onRefreshConversation?.();
            // Atualiza a lista lateral de conversas
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            // Invalida a RPC consumida pelo sidebar (contagem + preview de tags)
            queryClient.invalidateQueries({
              queryKey: [
                'contact-sidebar-details',
                conversation.contact?.id,
                conversation.inbox_id,
                conversation.id,
              ],
            });
          }}
        />
      )}
      
      
      {/* Dialog de detalhes de erro de mensagem */}
      <ErrorDetailsDialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          setSelectedErrorDetails(null);
        }}
        error={selectedErrorDetails?.error}
        messageType={selectedErrorDetails?.messageType}
        sentAt={selectedErrorDetails?.sentAt}
        providerSlug={selectedErrorDetails?.providerSlug}
      />
      
      {/* ===== WEBRTC SCREEN SHARE - VIEWER E MODAL ===== */}
      {/* Viewer flutuante de screen share - aparece quando conectado */}
      {/* IMPORTANTE: Não renderiza viewer quando modal fullscreen está aberto para evitar */}
      {/* dois <video> consumindo o mesmo MediaStream simultaneamente (causa tela preta) */}
      {showRemoteAccessButton && 
       agentScreenShare.sessionState === 'connected' && 
       agentScreenShare.remoteStream && 
       !showScreenShareModal && (
        <ScreenShareViewer
          stream={agentScreenShare.remoteStream}
          sessionState={agentScreenShare.sessionState}
          connectionState={agentScreenShare.connectionState}
          contactName={conversation?.contact?.name ?? 'Cliente'}
          onClose={agentScreenShare.endSession}
          onExpand={() => setShowScreenShareModal(true)}
        />
      )}

      {/* Modal fullscreen de screen share */}
      {showRemoteAccessButton && (
        <ScreenShareModal
          open={showScreenShareModal}
          onOpenChange={setShowScreenShareModal}
          stream={agentScreenShare.remoteStream}
          connectionState={agentScreenShare.connectionState}
          contactName={conversation?.contact?.name ?? 'Cliente'}
          onClose={() => {
            setShowScreenShareModal(false);
            agentScreenShare.endSession();
          }}
        />
      )}

      {/* ===== DIALOG DE AGUARDANDO RESPOSTA DO CLIENTE ===== */}
      {showRemoteAccessButton && agentScreenShare.sessionState === 'requesting' && (
        <ScreenShareWaitingDialog
          open={true}
          contactName={conversation?.contact?.name || conversation?.contact?.phone || 'Cliente'}
          onCancel={agentScreenShare.endSession}
        />
      )}

      {/* ===== SHEET DE PESQUISA CONTEXTUAL DE MENSAGENS (estilo WhatsApp Web) ===== */}
      <ConversationSearchSheet
        open={showSearchSheet}
        onOpenChange={setShowSearchSheet}
        conversationId={conversation?.id}
        jumpingMessageId={jumpingToMessageId ?? null}
        onResultClick={async (messageId) => {
          // 1) Se a mensagem já está na DOM (janela atual), apenas rola/destaca.
          const el = messageRefs.current.get(messageId);
          if (el) {
            scrollToMessage(messageId);
            return;
          }
          // 2) Caso contrário, carrega janela ancorada (~51 msgs) via RPC SaaS-scale.
          //    A modal de busca permanece aberta com spinner inline no item até concluir.
          if (onJumpToMessage) {
            const ok = await onJumpToMessage(messageId);
            if (ok) {
              // Marca o alvo pendente. O useEffect que observa `messages` rola
              // automaticamente quando o React commitar a nova janela e o ref
              // do elemento estiver disponível (sem race com setTimeout).
              pendingScrollTargetRef.current = messageId;
              pendingScrollAttemptsRef.current = 0;
            }
          } else {
            toast.info('Mensagem fora da janela carregada. Role o histórico para localizá-la.');
          }
        }}
      />

      {/* Sheet de encaminhamento (Fase 1 — WABA). Abre via menu de contexto. */}
      <ForwardMessageSheet
        open={forwardingMessages.length > 0}
        onOpenChange={(o) => { if (!o) setForwardingMessages([]); }}
        messages={forwardingMessages}
        sourceInboxId={conversation.inbox_id}
        sourceInboxName={conversation.inbox?.name || ''}
        onForwarded={() => {
          // Sucesso no envio: limpa Sheet e sai do modo seleção.
          setForwardingMessages([]);
          exitForwardSelection();
        }}
      />
    </div>
  );
}
